import Tippy from '@tippyjs/react'
import { PrimarySmallButton } from 'components/ReusableComponents/Button/SmallButtons'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import QuestionIcon from 'components/ReusableComponents/Icons/Question'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import Dropdown from 'components/ReusableComponents/ManageHotel/Dropdown'
import SmartDatepicker, {
  smartDateTypes
} from 'components/ReusableComponents/SmartDatepicker'
import UploadLogsDayRow from 'features/PMSLogs/DayRow'
import moment from 'moment-timezone'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import internalOnlyApi from 'services/api/internalOnly'
import pmsApi from 'services/api/pms'
import { getPepHealthCheckDisplay } from '../parsePepHealthCheckLastResult'
import { derivePepCsmAction } from '../derivePepCsmAction'
import {
  classifyPepCredentialFailureReason,
  parsePasswordExpireDays
} from '../classifyPepCredentialFailureReason'
import { matchCredentialDiagnosticCaptures } from './matchCredentialDiagnosticCaptures'
import {
  resolveDisplayActivityWindowsForUser,
  resolveInitialTimelineUserFilter,
  userHasTelemetrySignalsInRange
} from './electronTelemetryTimelineHelpers'
import {
  formatPepLoginUiOperationalMessage,
  isPepLoginUiTelemetryEvent,
  pepLoginUiEventShortLabel
} from './pepLoginUiOperationalMessage'
import {
  dashboardSessionEventShortLabel,
  formatDashboardSessionOperationalMessage,
  isDashboardSessionTelemetryEvent
} from './dashboardSessionOperationalMessage'
import {
  describePepDataPullFailClass,
  formatAppLifecycleOperationalMessage,
  formatPep403FailoverOperationalMessage,
  formatPepSessionOpsOperationalMessage,
  isAppLifecycleTelemetryEvent,
  isPepSessionOpsTelemetryEvent,
  isRangeOnlyPepDataPullFailure,
  pepElectronOpsEventShortLabel
} from './pepElectronOpsOperationalMessage'
import {
  formatHeartbeatOperationalMessage,
  heartbeatEventShortLabel,
  isHeartbeatTelemetryEvent
} from './heartbeatOperationalMessage'
import { deriveHotelTelemetrySituation } from './deriveHotelTelemetrySituation'
import { derivePepDataRhythm, buildPepDataRhythmInfoBar } from './derivePepDataRhythm'
import {
  buildTimelineViewRangeFromCalendar,
  formatHotelTime,
  formatTimelineBoundaryLabel,
  getHotelNow,
  getHotelTimeZoneLabel,
  getHotelTodayDateString,
  resolveHotelTimeZone
} from './formatHotelTime'
import { hotelLocalWallClockToUtcInstant } from './hotelLocalWallClockToUtcInstant'
import { buildPepLoginLadder, PEP_LADDER_STATUS } from './buildPepLoginLadder'
import {
  groupTelemetryRows,
  isLowValueInfraEvent
} from './telemetryTimelineGrouping'
import {
  captureMachineIdentity,
  normalizeDiagnosticCaptures
} from './diagnosticCapturesNormalizer'
import {
  buildDiagnosticArtifactFilename,
  DIAGNOSTIC_HTML_IFRAME_SANDBOX,
  downloadTextFile,
  getCaptureHar,
  getCapturePageHtml,
  isHarTruncated,
  isPageHtmlTruncated
} from './diagnosticCaptureArtifacts'
import { parseAccessibleHotels } from './accessibleHotelsParser'
import { buildElectronTelemetryFetchWindow } from './electronTelemetryFetchWindow'
import {
  normalizeActivityWindowInstants,
  normalizeDeviceInstants,
  normalizeTelemetryUserInstants,
  normalizeTimelineEventInstants,
  toUtcIsoInstant
} from './normalizeTelemetryInstants'

import styles from './ElectronTelemetryModal.module.scss'

const { today, last7days, last14days, last30days } = smartDateTypes

/** Max events loaded into the Event log table (newest first). */
const EventLogMaxLoadedRows = 100
/** Default number of rows shown until the user changes the control. */
const EventLogDefaultDisplayRows = 10

/** API load window for summary + Users & devices (Timeline date picker is client-only on this payload). */
const ELECTRON_TELEMETRY_FETCH_LOOKBACK_DAYS = 180

/** Client-side page size for the Diagnostic captures list so the modal stays short. */
const DIAGNOSTIC_CAPTURES_PAGE_SIZE = 5

/**
 * Minimum % of the swimlane width for an activity segment (vs. the selected date range).
 * Very short sessions stay visible/clickable; the segment title still shows exact start, end, and duration.
 */
const ActivityLaneMinSegmentPercent = 4

const eventLogRowCountOptions = [10, 25, 50, 75, 100]

const HELP_SUMMARY =
  'Total hotel users comes from the Dashboard user roster when the API sends `dashboardUsers`; otherwise from expected user count when available. “PEP app installed” counts roster members who have at least one PEP desktop signal in the loaded telemetry window (same window as the API load, not the Timeline date picker): a reporting device, a timeline event, or an activity window for their dashboard user id. “Not installed” counts roster members with no such signal in that window. Last activity is the latest telemetry time in that load.'

const HELP_TIMELINE =
  'Each swimlane is the full selected lookback (start/end at the top). Colored segments show when the desktop app sent telemetry (including data pulls, heartbeats, and login events); adjacent signals are merged into one bar. Striped red gaps mean no PEP desktop signal in that period (app closed, device off, or no network). “All users” stays selected at the top; when the modal opens (or after data reloads), the session bar that ended most recently is selected so the event log fills in. You can click another bar or a user chip to change the view. The event log loads up to 100 newest entries; Rows offers 10, 25, 50, 75, or 100 (default 10).'

const HELP_PEP_LOGIN_CREDENTIALS =
  'Hilton PEP login accounts stored for this hotel (merchant database). Usernames are shown in plain text. Passwords are encrypted at rest — copy the encrypted value for engineering or credential rotation workflows; do not treat it as the Hilton sign-in password.'

const HELP_USERS_DEVICES =
  'The table lists every dashboard user for this hotel (same roster as Users & Roles when the API sends `dashboardUsers`). It does not change when you change the Timeline date picker. Registered devices show one row each (version, Install, last seen, login flags). Under the device id, the most recent client event (any time) may appear when the API provides it. If a device is saved but has no telemetry anywhere in the loaded window, the badge reads “PEP desktop · Not in range.” Users with no registered device and no telemetry signal for their id in that window show “No app signal.” Rows with a device or telemetry signal are listed first; “No app signal” rows are last. Unassigned installs are included in the PEP desktop group when present.'

const HELP_DIAGNOSTIC_CAPTURES =
  'Diagnostic snapshots captured by the PEP desktop app on login attempts and critical PEP errors (permission denied, session expired, token verification failure, credential walk exhaustion, wrong/change password, password expiring, Available Properties or other post-login success pages, and unclassified timeout pages). Each row shows when it occurred (hotel local time), the event code and severity, the machine that captured it, an optional screenshot, optional View/Download HTML and HAR when the updated desktop app provided them, and the raw JSON context. Identical page HTML is not re-uploaded until the content changes. Internal staff captures are excluded.'

const HELP_GUEST_DATA_UPLOADS_BASE =
  'Same view as Add Guest Data -> Upload logs: pick a report tab and month. Each row is one day; expand with View reports for detail. Days are newest first, with pagination (10 days per page).'

const HELP_HEALTH_CHECK_DETAILS_GAP_CONTEXT =
  'Striped gaps mean no PEP desktop telemetry in that period. The app may have been closed, the PC off or asleep, the network unavailable, or the app removed — this view does not determine which case applied.'

function SectionInfoButton({ text }) {
  return (
    <Tippy
      content={text}
      placement="top"
      maxWidth={400}
      appendTo={() => document.body}
      zIndex={13000}
      delay={[100, 0]}
    >
      <button type="button" className={styles.sectionInfoBtn} aria-label={text}>
        <QuestionIcon className={styles.sectionInfoIcon} aria-hidden />
      </button>
    </Tippy>
  )
}

/** Maps API recentErrors rows into timeline event shape so they appear in the chart and activity log. */
function mapRecentErrorToTimelineEvent(err, index) {
  return {
    id: err.id ?? err.Id ?? `recent-error-${index}`,
    occurredAt: err.occurredAt ?? err.OccurredAt,
    dashboardUserId: err.dashboardUserId ?? err.DashboardUserId ?? null,
    eventType: err.eventCode ?? err.EventCode ?? 'Error',
    severity: err.severity ?? err.Severity ?? 'Error',
    message: err.message ?? err.Message ?? '—'
  }
}

function firstDefined(...values) {
  for (const v of values) {
    if (v !== undefined && v !== null) {
      return v
    }
  }
  return undefined
}

/** True when text describes a 403 or permission denial (e.g. getPepData fetch or API). */
function isLikelyAuthorizationDeniedMessage(text, ev) {
  if (ev != null && isRangeOnlyPepDataPullFailure(ev)) {
    return false
  }
  if (text == null || typeof text !== 'string') {
    return false
  }
  const lower = text.toLowerCase()
  if (lower.includes('failclass=range_')) {
    return false
  }
  if (
    lower.includes("don't have permission") ||
    lower.includes('you do not have permission') ||
    lower.includes('dont have permission')
  ) {
    return true
  }
  if (
    lower.includes('403') &&
    (lower.includes('authorization') || lower.includes('permission'))
  ) {
    return true
  }
  if (lower.includes('authorization error') && lower.includes('403')) {
    return true
  }
  return false
}

/**
 * api-dashboard may serialize JSON with PascalCase (Newtonsoft default); read both casings.
 */
function normalizePayload(raw) {
  const sum = raw?.summary ?? raw?.Summary ?? {}
  const versionBreakdown =
    sum.versionBreakdown ??
    sum.VersionBreakdown ??
    sum.versions ??
    sum.Versions ??
    raw?.versionBreakdown ??
    raw?.VersionBreakdown ??
    []

  const rawTimeline = Array.isArray(raw?.timelineEvents)
    ? raw.timelineEvents
    : Array.isArray(raw?.TimelineEvents)
    ? raw.TimelineEvents
    : []
  const rawRecentErrors = Array.isArray(raw?.recentErrors)
    ? raw.recentErrors
    : Array.isArray(raw?.RecentErrors)
    ? raw.RecentErrors
    : []
  const existingIds = new Set(
    rawTimeline.map(e => e?.id ?? e?.Id).filter(id => id != null && id !== '')
  )
  const fromRecentErrors = rawRecentErrors
    .map(mapRecentErrorToTimelineEvent)
    .filter((e, _idx) => {
      const id = e.id
      if (id != null && id !== '' && existingIds.has(id)) {
        return false
      }
      return true
    })
  const timelineEvents = [...rawTimeline, ...fromRecentErrors]

  const devicesRaw = raw?.devices ?? raw?.Devices
  const devices = Array.isArray(devicesRaw) ? devicesRaw : []

  const activityRaw = raw?.activityWindows ?? raw?.ActivityWindows
  const activityWindows = Array.isArray(activityRaw) ? activityRaw : []

  const telemetryUsersRaw = raw?.telemetryUsers ?? raw?.TelemetryUsers
  const telemetryUsers = Array.isArray(telemetryUsersRaw) ? telemetryUsersRaw : []

  const missingUsersRaw = raw?.missingUsers ?? raw?.MissingUsers
  const missingUsers = Array.isArray(missingUsersRaw) ? missingUsersRaw : []

  const dashboardUsersRaw = firstDefined(raw?.dashboardUsers, raw?.DashboardUsers)
  const dashboardUsers =
    dashboardUsersRaw !== undefined && dashboardUsersRaw !== null
      ? Array.isArray(dashboardUsersRaw)
        ? dashboardUsersRaw
        : []
      : null

  const deviceLinkedRaw = firstDefined(
    raw?.deviceLinkedUserProfiles,
    raw?.DeviceLinkedUserProfiles
  )
  const deviceLinkedUserProfiles =
    deviceLinkedRaw !== undefined && deviceLinkedRaw !== null
      ? Array.isArray(deviceLinkedRaw)
        ? deviceLinkedRaw
        : []
      : []

  const expectedRaw = firstDefined(raw?.expectedUserCount, raw?.ExpectedUserCount)

  const internalGlobalRaw = firstDefined(
    raw?.internalGlobalDashboardUserIds,
    raw?.InternalGlobalDashboardUserIds
  )
  const internalGlobalDashboardUserIds = Array.isArray(internalGlobalRaw)
    ? internalGlobalRaw.map(id => String(id)).filter(id => id !== '')
    : []
  const internalIdSet =
    internalGlobalDashboardUserIds.length > 0
      ? new Set(internalGlobalDashboardUserIds)
      : null

  /** Internal ids that still have a PEP device row for this hotel — keep their timeline/telemetry. */
  const internalIdsWithHotelDevice = new Set()
  if (internalIdSet) {
    for (const d of devices) {
      const uid = d.dashboardUserId ?? d.DashboardUserId
      if (uid != null && internalIdSet.has(String(uid))) {
        internalIdsWithHotelDevice.add(String(uid))
      }
    }
  }

  const stripIfInternalRow = row => {
    if (!internalIdSet) {
      return true
    }
    const uid = row.dashboardUserId ?? row.DashboardUserId
    if (uid == null || uid === undefined) {
      return true
    }
    const ks = String(uid)
    if (!internalIdSet.has(ks)) {
      return true
    }
    return internalIdsWithHotelDevice.has(ks)
  }

  const timelineEventsFinal = internalIdSet
    ? timelineEvents.filter(stripIfInternalRow)
    : timelineEvents
  const activityWindowsFinal = internalIdSet
    ? activityWindows.filter(stripIfInternalRow)
    : activityWindows
  const telemetryUsersFinal = internalIdSet
    ? telemetryUsers.filter(stripIfInternalRow)
    : telemetryUsers
  /**
   * Keep all devices returned for this hotel. Internal/global dashboard ids are stripped from the
   * timeline only so CSMs do not see cross-hotel activity there; hiding devices broke the Users &
   * devices table when the PEP install is tied to an id not on the hotel roster.
   */
  const devicesFinal = devices

  return {
    summary: {
      deviceCount: sum.deviceCount ?? sum.DeviceCount ?? 0,
      distinctUserCount: sum.distinctUserCount ?? sum.DistinctUserCount ?? 0,
      lastActivityAt: toUtcIsoInstant(sum.lastActivityAt ?? sum.LastActivityAt ?? null),
      openErrorCount: sum.openErrorCount ?? sum.OpenErrorCount ?? 0,
      versionBreakdown: Array.isArray(versionBreakdown) ? versionBreakdown : []
    },
    expectedUserCount:
      expectedRaw !== undefined && expectedRaw !== null ? expectedRaw : null,
    telemetryUsers: normalizeTelemetryUserInstants(telemetryUsersFinal),
    missingUsers,
    dashboardUsers,
    deviceLinkedUserProfiles,
    devices: normalizeDeviceInstants(devicesFinal),
    timelineEvents: normalizeTimelineEventInstants(timelineEventsFinal),
    activityWindows: normalizeActivityWindowInstants(activityWindowsFinal),
    internalGlobalDashboardUserIds,
    internalIdsWithHotelDevice: Array.from(internalIdsWithHotelDevice),
    hotelTimeZoneId: raw?.hotelTimeZoneId ?? raw?.HotelTimeZoneId ?? null,
    hotelUtcOffsetMinutes:
      raw?.hotelUtcOffsetMinutes ?? raw?.HotelUtcOffsetMinutes ?? null,
    gapThresholdMinutes: raw?.gapThresholdMinutes ?? raw?.GapThresholdMinutes ?? 15,
    pepLoginCredentials: (() => {
      const rawCreds = raw?.pepLoginCredentials ?? raw?.PepLoginCredentials ?? []
      if (!Array.isArray(rawCreds)) {
        return []
      }
      return rawCreds.map(c => ({
        id: c.id ?? c.Id,
        directfulHotelName: c.directfulHotelName ?? c.DirectfulHotelName ?? '',
        pepHotelName: c.pepHotelName ?? c.PepHotelName ?? '',
        username: c.username ?? c.Username ?? '',
        encryptedPassword: c.encryptedPassword ?? c.EncryptedPassword ?? '',
        propertyId: c.propertyId ?? c.PropertyId ?? '',
        apiBaseUrl: c.apiBaseUrl ?? c.ApiBaseUrl ?? '',
        pepLoginUrl: c.pepLoginUrl ?? c.PepLoginUrl ?? '',
        isPrimary: c.isPrimary ?? c.IsPrimary ?? false,
        status: c.status ?? c.Status ?? '',
        credentialRevision: c.credentialRevision ?? c.CredentialRevision ?? 0,
        lastFailureReason: c.lastFailureReason ?? c.LastFailureReason ?? '',
        lastFailureAt: toUtcIsoInstant(c.lastFailureAt ?? c.LastFailureAt ?? null)
      }))
    })()
  }
}

async function copyTelemetryCredentialField(value, fieldLabel) {
  const text = value == null ? '' : String(value)
  try {
    await navigator.clipboard.writeText(text)
  } catch {
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.setAttribute('readonly', '')
    textarea.style.position = 'fixed'
    textarea.style.left = '-9999px'
    document.body.appendChild(textarea)
    textarea.select()
    document.execCommand('copy')
    document.body.removeChild(textarea)
  }
  return fieldLabel
}

function deriveTelemetryUsersFromDevices(devices, timelineEvents, dashboardUsers) {
  if (!devices?.length) {
    return []
  }

  const map = new Map()
  for (const d of devices) {
    const id = d.dashboardUserId ?? d.DashboardUserId
    const key = id == null || id === undefined ? 'unassigned' : String(id)
    if (!map.has(key)) {
      const roster =
        id != null ? getRosterEntryForDashboardUserId(id, dashboardUsers) : null
      const defaultName =
        id == null ? 'Unassigned installation' : roster?.displayName || `User ${id}`
      map.set(key, {
        dashboardUserId: id,
        displayName: defaultName,
        email: roster?.email ?? null,
        lastActivityAt: d.lastSeenAt ?? d.LastSeenAt,
        deviceCount: 0,
        eventCount: 0,
        isUnassigned: id == null || id === undefined
      })
    }
    const u = map.get(key)
    u.deviceCount += 1
    const ls = d.lastSeenAt ?? d.LastSeenAt
    if (ls && moment(ls).isValid()) {
      if (!u.lastActivityAt || moment(ls).isAfter(moment(u.lastActivityAt))) {
        u.lastActivityAt = ls
      }
    }
  }

  const evByUser = new Map()
  for (const ev of timelineEvents || []) {
    const id = ev.dashboardUserId ?? ev.DashboardUserId
    const key = id == null || id === undefined ? 'unassigned' : String(id)
    evByUser.set(key, (evByUser.get(key) || 0) + 1)
  }
  for (const [k, u] of map.entries()) {
    u.eventCount = evByUser.get(k) || 0
  }

  return Array.from(map.values())
}

function enrichTelemetryUsersWithRoster(telemetryUsers, dashboardUsers) {
  if (!telemetryUsers?.length || !dashboardUsers?.length) {
    return telemetryUsers ?? []
  }
  return telemetryUsers.map(u => {
    const id = u.dashboardUserId ?? u.DashboardUserId
    if (id == null || id === undefined) {
      return u
    }
    const roster = getRosterEntryForDashboardUserId(id, dashboardUsers)
    if (!roster) {
      return u
    }
    const name =
      (roster.displayName && roster.displayName.trim()) ||
      u.displayName ||
      u.DisplayName ||
      `User ${id}`
    const em = (roster.email || u.email || u.Email || '').trim() || null
    return {
      ...u,
      displayName: name,
      DisplayName: name,
      email: em,
      Email: em
    }
  })
}

function filterOutInternalGlobalTelemetryUsers(
  telemetryUsers,
  internalGlobalDashboardUserIds,
  devices
) {
  if (!internalGlobalDashboardUserIds?.length) {
    return telemetryUsers || []
  }
  const s = new Set(internalGlobalDashboardUserIds.map(id => String(id)))
  const keep = new Set()
  for (const d of devices || []) {
    const uid = d.dashboardUserId ?? d.DashboardUserId
    if (uid != null && s.has(String(uid))) {
      keep.add(String(uid))
    }
  }
  return (telemetryUsers || []).filter(u => {
    const id = u.dashboardUserId ?? u.DashboardUserId
    if (id == null || id === undefined) {
      return true
    }
    const ks = String(id)
    if (!s.has(ks)) {
      return true
    }
    return keep.has(ks)
  })
}

function mergedRosterProfiles(dashboardUsers, deviceLinkedUserProfiles) {
  const linkedArr = Array.isArray(deviceLinkedUserProfiles)
    ? deviceLinkedUserProfiles
    : []
  if (dashboardUsers != null && Array.isArray(dashboardUsers)) {
    return [...dashboardUsers, ...linkedArr]
  }
  return linkedArr
}

function resolveTelemetryUsersForPayload(payload) {
  if (!payload) {
    return []
  }
  const rosterForNames = mergedRosterProfiles(
    payload.dashboardUsers,
    payload.deviceLinkedUserProfiles
  )
  let list = []
  if (payload.telemetryUsers?.length) {
    list = payload.telemetryUsers
  } else {
    list = deriveTelemetryUsersFromDevices(
      payload.devices,
      payload.timelineEvents,
      rosterForNames
    )
  }
  const enriched = enrichTelemetryUsersWithRoster(list, rosterForNames)
  return filterOutInternalGlobalTelemetryUsers(
    enriched,
    payload.internalGlobalDashboardUserIds,
    payload.devices
  )
}

function timelineUserKeyFromTelemetryUser(u) {
  if (u.isUnassigned || u.dashboardUserId == null || u.dashboardUserId === undefined) {
    return 'unassigned'
  }
  return String(u.dashboardUserId)
}

function displayNameForTimelineUserKey(userKey, telemetryUsersResolved, dashboardUsers) {
  if (userKey === 'unassigned') {
    return 'Unassigned installation'
  }
  const roster = getRosterEntryForDashboardUserId(userKey, dashboardUsers)
  if (roster?.displayName && String(roster.displayName).trim() !== '') {
    return roster.displayName
  }
  const u = telemetryUsersResolved.find(
    tu => timelineUserKeyFromTelemetryUser(tu) === userKey
  )
  const fromTelemetry = u?.displayName ?? u?.DisplayName
  if (fromTelemetry && String(fromTelemetry).trim() !== '') {
    return fromTelemetry
  }
  return `User ${userKey}`
}

function eventMatchesTimelineFilter(ev, filterKey) {
  if (filterKey === 'all') {
    return true
  }
  const uid = ev.dashboardUserId ?? ev.DashboardUserId
  if (filterKey === 'unassigned') {
    return uid == null || uid === undefined
  }
  return String(uid) === filterKey
}

/**
 * Same as strict user id match, plus: events with no user id are included for a person if the
 * event time falls inside one of that user's activity windows (common when telemetry omits user).
 */
function eventMatchesTimelineFilterWithSessionAttribution(
  ev,
  filterKey,
  activityWindowsForAttribution
) {
  if (filterKey === 'all') {
    return true
  }
  if (eventMatchesTimelineFilter(ev, filterKey)) {
    return true
  }
  const uid = ev.dashboardUserId ?? ev.DashboardUserId
  if (uid != null && uid !== undefined) {
    return false
  }
  if (filterKey === 'unassigned') {
    return true
  }
  const t = moment(ev.occurredAt ?? ev.OccurredAt)
  if (!t.isValid()) {
    return false
  }
  for (const w of activityWindowsForAttribution || []) {
    if (activityWindowUserKey(w) !== filterKey) {
      continue
    }
    const ws = moment(w.startAt ?? w.StartAt)
    const we = moment(w.endAt ?? w.EndAt)
    if (ws.isValid() && we.isValid() && t.isBetween(ws, we, null, '[]')) {
      return true
    }
  }
  return false
}

/**
 * Returns { left, width } as percentages along the selected timeline [rangeStart, rangeEnd].
 * Clips each activity window to that range, then applies width proportional to elapsed time there.
 */
function rangeLeftWidthPercent(startAt, endAt, rangeStart, rangeEnd) {
  const s = moment(startAt)
  const e = moment(endAt)
  if (!s.isValid() || !e.isValid()) {
    return { left: 0, width: 0 }
  }
  const rsMs = rangeStart.valueOf()
  const reMs = rangeEnd.valueOf()
  if (reMs <= rsMs) {
    return { left: 0, width: 0 }
  }
  const clipS = Math.max(s.valueOf(), rsMs)
  const clipE = Math.min(e.valueOf(), reMs)
  if (clipE <= clipS) {
    return { left: 0, width: 0 }
  }
  const span = reMs - rsMs
  let leftPct = ((clipS - rsMs) / span) * 100
  const widthLogicalPct = ((clipE - clipS) / span) * 100
  let widthPct = Math.max(widthLogicalPct, ActivityLaneMinSegmentPercent)
  widthPct = Math.min(widthPct, 100)
  let left = Math.min(100, Math.max(0, leftPct))
  if (left + widthPct > 100) {
    left = Math.max(0, 100 - widthPct)
  }
  return { left, width: widthPct }
}

function clipActivityWindowToTimelineRange(win, rangeStart, rangeEnd) {
  const s = moment(win.startAt ?? win.StartAt)
  const e = moment(win.endAt ?? win.EndAt)
  if (!s.isValid() || !e.isValid()) {
    return { clipStart: null, clipEnd: null }
  }
  return {
    clipStart: moment.max(s, rangeStart),
    clipEnd: moment.min(e, rangeEnd)
  }
}

function activityWindowUserKey(w) {
  const id = w.dashboardUserId ?? w.DashboardUserId
  return id == null || id === undefined ? 'unassigned' : String(id)
}

function windowsForUserKey(activityWindows, userKey) {
  return (activityWindows || []).filter(w => activityWindowUserKey(w) === userKey)
}

function isSameActivityWindow(a, b) {
  if (!a || !b) {
    return false
  }
  const ida = a.id ?? a.Id
  const idb = b.id ?? b.Id
  if (ida != null && idb != null) {
    return ida === idb
  }
  return (
    (a.startAt ?? a.StartAt) === (b.startAt ?? b.StartAt) &&
    (a.endAt ?? a.EndAt) === (b.endAt ?? b.EndAt) &&
    activityWindowUserKey(a) === activityWindowUserKey(b)
  )
}

function logSeverityLevel(ev) {
  const s = (ev.severity ?? ev.Severity ?? 'Info').toString().toLowerCase()
  if (s === 'error' || s === 'fatal') {
    return 'error'
  }
  if (s === 'warn' || s === 'warning') {
    return 'warn'
  }
  return 'info'
}

function severityDisplayLabel(ev) {
  const s = (ev.severity ?? ev.Severity ?? 'Info').toString()
  if (logSeverityLevel(ev) === 'error') {
    return 'Error'
  }
  if (logSeverityLevel(ev) === 'warn') {
    return 'Warning'
  }
  return s === '' ? 'Info' : s
}

const PepPullMessageKeys = [
  'source',
  'mode',
  'op',
  'phase',
  'http',
  'resource',
  'url',
  'request',
  'range',
  'reportStatus',
  'propertyId',
  'failClass',
  'err'
]

function parseTaggedMessage(raw, keysInOrder) {
  if (!raw || typeof raw !== 'string') {
    return null
  }
  const keyPositions = keysInOrder
    .map(key => ({ key, idx: raw.indexOf(`${key}=`) }))
    .filter(k => k.idx >= 0)
    .sort((a, b) => a.idx - b.idx)
  if (keyPositions.length === 0) {
    return null
  }
  const parsed = {}
  for (let i = 0; i < keyPositions.length; i += 1) {
    const curr = keyPositions[i]
    const next = keyPositions[i + 1]
    const valueStart = curr.idx + curr.key.length + 1
    const valueEnd = next ? next.idx : raw.length
    parsed[curr.key] = raw.slice(valueStart, valueEnd).trim()
  }
  return parsed
}

function summarizePepRequestPayload(requestRaw) {
  if (!requestRaw) {
    return null
  }
  try {
    const p = JSON.parse(requestRaw)
    const range =
      p.startDate && p.endDate ? `${p.startDate} -> ${p.endDate}` : p.startDate || p.endDate || '-'
    const onboarding =
      p.isOnboarding === true ? 'yes' : p.isOnboarding === false ? 'no' : '-'
    return `property ${p.propertyId || '-'} | range ${range} | status ${p.reportStatus || '-'} | onboarding ${onboarding}`
  } catch (_err) {
    return requestRaw
  }
}

function renderPepPullTechnicalMessage(raw) {
  const tagged = parseTaggedMessage(raw, PepPullMessageKeys)
  if (!tagged) {
    return raw
  }

  const titleMode = tagged.mode || 'unknown'
  const titleOperation = tagged.op || 'operation'
  const titleHttp = tagged.http ? `HTTP ${tagged.http}` : null
  const requestSummary = summarizePepRequestPayload(tagged.request)
  const failClassHint = describePepDataPullFailClass(tagged.failClass)

  return (
    <div className={styles.opsMessageStack}>
      {failClassHint && (
        <div className={styles.opsMessageLine}>{failClassHint}</div>
      )}
      <div className={styles.opsMessageHeadline}>
        <span className={styles.opsMessageBadge}>
          {titleMode} · {titleOperation}
        </span>
        {titleHttp && <span className={styles.opsMessageMeta}>{titleHttp}</span>}
        {tagged.phase && <span className={styles.opsMessageMeta}>phase {tagged.phase}</span>}
        {tagged.failClass && (
          <span className={styles.opsMessageMeta}>failClass {tagged.failClass}</span>
        )}
      </div>
      <div className={styles.opsMessageGrid}>
        {tagged.range && (
          <span className={styles.opsMessageItem}>
            <strong>Range:</strong> {tagged.range}
          </span>
        )}
        {tagged.reportStatus && (
          <span className={styles.opsMessageItem}>
            <strong>Report:</strong> {tagged.reportStatus}
          </span>
        )}
        {tagged.propertyId && (
          <span className={styles.opsMessageItem}>
            <strong>Property:</strong> {tagged.propertyId}
          </span>
        )}
      </div>
      {tagged.resource && (
        <div className={styles.opsMessageLine}>
          <strong>Resource:</strong> <span className={styles.opsMessageCode}>{tagged.resource}</span>
        </div>
      )}
      {tagged.url && (
        <div className={styles.opsMessageLine}>
          <strong>URL:</strong> <span className={styles.opsMessageCode}>{tagged.url}</span>
        </div>
      )}
      {requestSummary && (
        <div className={styles.opsMessageLine}>
          <strong>Request:</strong> <span className={styles.opsMessageCode}>{requestSummary}</span>
        </div>
      )}
      {tagged.err && (
        <div className={styles.opsMessageError}>
          <strong>Error:</strong> {tagged.err}
        </div>
      )}
    </div>
  )
}

function telemetryEventTypeLabel(ev) {
  const type = (ev.eventType ?? ev.EventType ?? '—').trim() || '—'
  const code = (ev.eventCode ?? ev.EventCode ?? '').trim()
  const pepLabel = pepLoginUiEventShortLabel(code)
  if (pepLabel) {
    return `PEP login · ${pepLabel}`
  }
  const dashboardLabel = dashboardSessionEventShortLabel(code)
  if (dashboardLabel) {
    return `Dashboard · ${dashboardLabel}`
  }
  if (isHeartbeatTelemetryEvent(ev)) {
    return heartbeatEventShortLabel()
  }
  const opsLabel = pepElectronOpsEventShortLabel(code)
  if (opsLabel) {
    return opsLabel
  }
  if (!code) {
    return type
  }
  return `${type} · ${code}`
}

/** Short operational summary for support / hotel ops (English). */
function operationalMessage(ev) {
  const raw = (ev.message ?? ev.Message ?? '').trim()
  const type = (ev.eventType ?? ev.EventType ?? '').trim()
  const lower = type.toLowerCase()

  if (lower.includes('datapullsucceeded') || lower.includes('data_pull_succeeded')) {
    return (
      raw ||
      'Data was pulled successfully from PEP (reports or onboarding). The app can reach the portal.'
    )
  }
  if (lower.includes('datapullstarted') || lower.includes('data_pull_started')) {
    return raw || 'A scheduled or manual data pull from PEP has started.'
  }
  if (isPepLoginUiTelemetryEvent(ev)) {
    return formatPepLoginUiOperationalMessage(ev)
  }
  if (isDashboardSessionTelemetryEvent(ev)) {
    return formatDashboardSessionOperationalMessage(ev)
  }
  if (isHeartbeatTelemetryEvent(ev)) {
    return formatHeartbeatOperationalMessage(ev)
  }
  if (isAppLifecycleTelemetryEvent(ev)) {
    return formatAppLifecycleOperationalMessage(ev)
  }
  if (isPepSessionOpsTelemetryEvent(ev)) {
    return formatPepSessionOpsOperationalMessage(ev)
  }
  if (type === 'Pep403Failover' || (ev.eventCode ?? ev.EventCode ?? '').includes('PEP_403_FAILOVER')) {
    return formatPep403FailoverOperationalMessage(ev)
  }
  const pullCode = (ev.eventCode ?? ev.EventCode ?? '').trim()
  if (pullCode === 'PEP_DATA_PULL_FAIL_403_SESSION') {
    return `${
      describePepDataPullFailClass('session_auth') ||
      'PEP data pull failed because the Hilton PEP session appears expired.'
    } The desktop app retries automatically on the next scheduled run.`
  }
  if (pullCode === 'PEP_DATA_PULL_FAIL_403_RANGE') {
    return (
      describePepDataPullFailClass('range_depth') ||
      'PEP data pull failed for an older or edge date range; this is usually not a sign-in issue.'
    )
  }
  if (pullCode === 'PEP_DATA_PULL_FAIL_401') {
    return 'PEP data pull failed because Hilton PEP rejected the sign-in (401). The desktop app retries automatically on the next scheduled run.'
  }
  if (pullCode === 'PEP_DATA_PULL_FAIL_403') {
    return 'PEP data pull failed with an authorization error (403). The desktop app retries automatically on the next scheduled run.'
  }
  if (pullCode === 'PEP_DATA_PULL_FAIL') {
    return 'PEP data pull failed. The desktop app retries automatically on the next scheduled run.'
  }
  if (type === 'PEP_LOGIN_RETRY' || lower.includes('pep_login_retry')) {
    return (
      raw ||
      'PEP portal sign-in had to retry once. If this repeats, check network or PEP availability.'
    )
  }
  if (lower.includes('heartbeat')) {
    return formatHeartbeatOperationalMessage(ev)
  }
  if (lower.includes('gap')) {
    return raw || 'No sync activity was seen for longer than the expected gap threshold.'
  }
  if (raw.includes('source=getPepData') && raw.includes('op=')) {
    return renderPepPullTechnicalMessage(raw)
  }
  if (logSeverityLevel(ev) === 'error') {
    return (
      raw || 'An error was reported; see the technical message if details are missing.'
    )
  }
  return raw || type || 'Telemetry event.'
}

/**
 * Stable signature used to collapse repeated timeline rows. Two events share a row only when their
 * displayed type label, event code, and human operational message all match, so distinct activity
 * is never merged while repetitive chatter (keep-alive, recovery suppressed, permission denied)
 * folds into a single counted row.
 */
function timelineRowSignature(ev) {
  const code = (ev.eventCode ?? ev.EventCode ?? '').toString().trim()
  const device = (ev.deviceInstallationId ?? ev.DeviceInstallationId ?? '').toString().trim()
  return `${telemetryEventTypeLabel(ev)}||${code}||${operationalMessage(ev)}||${device}`
}

/**
 * Span covering all telemetry in the loaded payload, for Users & devices badges and install flags.
 * Independent of the Timeline date picker.
 */
function buildFullPayloadSignalRange(data) {
  const events = data.timelineEvents || []
  const windows = data.activityWindows || []
  const devices = data.devices || []
  let minT = null
  let maxT = null
  const bump = m => {
    if (!m || !m.isValid()) {
      return
    }
    if (!minT || m.isBefore(minT)) {
      minT = m.clone()
    }
    if (!maxT || m.isAfter(maxT)) {
      maxT = m.clone()
    }
  }
  for (const ev of events) {
    bump(moment(ev.occurredAt ?? ev.OccurredAt))
  }
  for (const w of windows) {
    bump(moment(w.startAt ?? w.StartAt))
    bump(moment(w.endAt ?? w.EndAt))
  }
  for (const d of devices) {
    bump(moment(d.firstLogAt ?? d.FirstLogAt ?? d.firstSeenAt ?? d.FirstSeenAt))
    bump(moment(d.lastSeenAt ?? d.LastSeenAt ?? d.lastSeenOn ?? d.LastSeenOn))
  }
  const now = moment()
  if (!minT || !maxT) {
    return { start: now.clone().subtract(10, 'years'), end: now.clone().add(1, 'day') }
  }
  return { start: minT.clone().subtract(1, 'hour'), end: maxT.clone().add(1, 'hour') }
}

function eventInTimelineView(ev, rangeStart, rangeEnd) {
  const t = moment(ev.occurredAt ?? ev.OccurredAt)
  return t.isValid() && t.isBetween(rangeStart, rangeEnd, null, '[]')
}

function deviceHasTelemetryInSelectedRange(
  deviceId,
  timelineEvents,
  rangeStart,
  rangeEnd
) {
  if (deviceId == null || deviceId === '' || deviceId === '—') {
    return false
  }
  for (const ev of timelineEvents || []) {
    const did = ev.deviceInstallationId ?? ev.DeviceInstallationId
    if (!did || String(did) !== String(deviceId)) {
      continue
    }
    if (eventInTimelineView(ev, rangeStart, rangeEnd)) {
      return true
    }
  }
  return false
}

function attachDeviceInstallSignalFlags(row, timelineRange, timelineEvents) {
  if (row.kind !== 'device') {
    return row
  }
  if (!timelineRange?.start || !timelineRange?.end) {
    return { ...row, deviceSignalInSelectedRange: true }
  }
  return {
    ...row,
    deviceSignalInSelectedRange: deviceHasTelemetryInSelectedRange(
      row.deviceId,
      timelineEvents,
      timelineRange.start,
      timelineRange.end
    )
  }
}

function activityWindowOverlapsTimeline(w, rangeStart, rangeEnd) {
  const ws = moment(w.startAt ?? w.StartAt)
  const we = moment(w.endAt ?? w.EndAt)
  if (!ws.isValid() || !we.isValid()) {
    return false
  }
  return !we.isBefore(rangeStart, 'minute') && !ws.isAfter(rangeEnd, 'minute')
}

function resolveLatestSignalMomentFromPayload(normalized) {
  if (!normalized) {
    return null
  }
  let maxM = null
  const bump = m => {
    if (!m || !m.isValid()) {
      return
    }
    if (!maxM || m.isAfter(maxM)) {
      maxM = m.clone()
    }
  }
  const sum = normalized.summary || {}
  bump(moment(sum.lastActivityAt ?? sum.LastActivityAt))
  for (const d of normalized.devices || []) {
    bump(moment(d.lastSeenAt ?? d.LastSeenAt ?? d.lastSeenOn ?? d.LastSeenOn))
  }
  for (const w of normalized.activityWindows || []) {
    bump(moment(w.endAt ?? w.EndAt))
    bump(moment(w.startAt ?? w.StartAt))
  }
  for (const ev of normalized.timelineEvents || []) {
    bump(moment(ev.occurredAt ?? ev.OccurredAt))
  }
  return maxM
}

function telemetryUsersForInitialFilter(normalized) {
  if (normalized.telemetryUsers?.length) {
    return normalized.telemetryUsers
  }
  return deriveTelemetryUsersFromDevices(
    normalized.devices,
    normalized.timelineEvents,
    normalized.dashboardUsers
  )
}

function resolveTimelineOpenFocus(normalized, hotelTodayStr, { spanFromLastSignalDay = false } = {}) {
  const hotelTimeZoneId = normalized?.hotelTimeZoneId ?? normalized?.HotelTimeZoneId ?? null
  const hotelZone = resolveHotelTimeZone(hotelTimeZoneId)
  const gapMinutes = normalized.gapThresholdMinutes ?? 15
  const now = getHotelNow(hotelTimeZoneId)
  const defaultFocus = {
    fromStr: hotelTodayStr,
    toStr: hotelTodayStr,
    userFilter: 'all',
    eventLogRowLimit: EventLogDefaultDisplayRows
  }

  let calendarFrom = hotelTodayStr
  let calendarTo = hotelTodayStr

  if (spanFromLastSignalDay) {
    const latest = resolveLatestSignalMomentFromPayload(normalized)
    if (!latest?.isValid()) {
      return defaultFocus
    }
    calendarFrom = hotelZone
      ? latest.clone().tz(hotelZone).startOf('day').format('YYYY-MM-DD')
      : latest.clone().startOf('day').format('YYYY-MM-DD')
    if (moment(calendarFrom, 'YYYY-MM-DD').isAfter(moment(calendarTo, 'YYYY-MM-DD'), 'day')) {
      calendarTo = calendarFrom
    }
  }

  const viewRange = buildTimelineViewRangeFromCalendar(
    calendarFrom,
    calendarTo,
    hotelTimeZoneId
  )
  const eventsForFocus = (normalized.timelineEvents || []).filter(ev =>
    eventInTimelineView(ev, viewRange.start, viewRange.end)
  )
  const userKeys = new Set()
  for (const u of telemetryUsersForInitialFilter(normalized)) {
    userKeys.add(timelineUserKeyFromTelemetryUser(u))
  }
  const apiWindows = (normalized.activityWindows || []).filter(w =>
    activityWindowOverlapsTimeline(w, viewRange.start, viewRange.end)
  )
  const mergedForFocus = []
  for (const userKey of userKeys) {
    mergedForFocus.push(
      ...resolveDisplayActivityWindowsForUser(
        userKey,
        windowsForUserKey(apiWindows, userKey),
        eventsForFocus,
        apiWindows,
        gapMinutes
      )
    )
  }

  const userFilter = resolveInitialTimelineUserFilter({
    telemetryUsers: telemetryUsersForInitialFilter(normalized),
    activityWindowsInView: mergedForFocus,
    now,
    gapThresholdMinutes: gapMinutes
  })

  return {
    fromStr: calendarFrom,
    toStr: calendarTo,
    userFilter,
    eventLogRowLimit: spanFromLastSignalDay
      ? Math.min(50, EventLogMaxLoadedRows)
      : EventLogDefaultDisplayRows
  }
}

/** Matches Add Guest Data → Upload logs (PMSLogs) page size for the flat reports table. */
const PMS_UPLOAD_HISTORY_PAGE_SIZE = 10

const PMS_UPLOAD_HISTORY_MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
]

function normalizePmsHistoryDayGroupForUploadLogs(day) {
  if (!day || typeof day !== 'object') {
    return {
      date: '',
      totalRecords: 0,
      totalSubscribers: 0,
      totalNewSubscribers: 0,
      totalUpdatedSubscribers: 0,
      totalDuplicateSubscribers: 0,
      totalUnUsableSubscribers: 0,
      totalInProgressSubscribers: 0,
      logs: []
    }
  }
  return {
    date: day.date ?? day.Date ?? '',
    totalRecords: day.totalRecords ?? day.TotalRecords ?? 0,
    totalSubscribers: day.totalSubscribers ?? day.TotalSubscribers ?? 0,
    totalNewSubscribers: day.totalNewSubscribers ?? day.TotalNewSubscribers ?? 0,
    totalUpdatedSubscribers:
      day.totalUpdatedSubscribers ?? day.TotalUpdatedSubscribers ?? 0,
    totalDuplicateSubscribers:
      day.totalDuplicateSubscribers ?? day.TotalDuplicateSubscribers ?? 0,
    totalUnUsableSubscribers:
      day.totalUnUsableSubscribers ?? day.TotalUnUsableSubscribers ?? 0,
    totalInProgressSubscribers:
      day.totalInProgressSubscribers ?? day.TotalInProgressSubscribers ?? 0,
    logs: day.logs ?? day.Logs ?? []
  }
}

/** Newest-first merged stream of events and activity sessions for one user key (optional view range). */
function buildUserActivityLogItems(userKey, allEvents, allWindows, rangeStart, rangeEnd) {
  const items = []
  const rs = rangeStart
  const re = rangeEnd

  for (const ev of allEvents || []) {
    if (!eventMatchesTimelineFilterWithSessionAttribution(ev, userKey, allWindows)) {
      continue
    }
    const t = ev.occurredAt ?? ev.OccurredAt
    const mt = moment(t)
    if (!mt.isValid()) {
      continue
    }
    if (rs && re && !mt.isBetween(rs, re, null, '[]')) {
      continue
    }
    items.push({ sortKey: mt.valueOf(), kind: 'event', data: ev })
  }
  for (const w of windowsForUserKey(allWindows, userKey)) {
    const startAt = w.startAt ?? w.StartAt
    const endAt = w.endAt ?? w.EndAt
    const ms = moment(startAt)
    const me = moment(endAt)
    if (!ms.isValid() || !me.isValid()) {
      continue
    }
    if (rs && re && (me.isBefore(rs, 'minute') || ms.isAfter(re, 'minute'))) {
      continue
    }
    items.push({ sortKey: me.valueOf(), kind: 'session', data: w })
  }
  return items.sort((a, b) => b.sortKey - a.sortKey)
}

function markerColorForUserKey(userKey, index) {
  const palette = ['#3182ce', '#38a169', '#d69e2e', '#805ad5', '#dd6b20', '#319795']
  if (userKey === 'unassigned') {
    return '#718096'
  }
  if (userKey === 'all') {
    return palette[0]
  }
  let h = 0
  for (let i = 0; i < userKey.length; i += 1) {
    h = (h + userKey.charCodeAt(i) * (i + 1)) % palette.length
  }
  return palette[(h + index) % palette.length]
}

/** Short label for the Users table so the row stays one line; full id remains in title. */
function formatDeviceInstallationIdForTable(deviceInstallationId) {
  if (deviceInstallationId == null || deviceInstallationId === '') {
    return null
  }
  const s = String(deviceInstallationId).trim()
  if (s.length <= 24) {
    return s
  }
  return `${s.slice(0, 8)}…${s.slice(-6)}`
}

/**
 * Human machine identity for a device row: "OsUserName @ MachineName".
 * Falls back to whichever part is present; returns null when neither is known
 * so the UI keeps showing the device installation id instead.
 */
function formatDeviceIdentity(osUserName, machineName) {
  const user = (osUserName == null ? '' : String(osUserName)).trim()
  const machine = (machineName == null ? '' : String(machineName)).trim()
  if (user && machine) {
    return `${user} @ ${machine}`
  }
  if (machine) {
    return machine
  }
  if (user) {
    return user
  }
  return null
}

/** Human-readable duration for titles (logical span; lane may use a minimum width for visibility). */
function formatDurationBetween(startAt, endAt) {
  const s = moment(startAt)
  const e = moment(endAt)
  if (!s.isValid() || !e.isValid()) {
    return '—'
  }
  const mins = Math.max(0, e.diff(s, 'minutes'))
  if (mins < 60) {
    return `${mins} min`
  }
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return m === 0 ? `${h}h` : `${h}h ${m}m`
}

function idsEqualNullable(a, b) {
  if (a == null && b == null) {
    return true
  }
  if (a == null || b == null) {
    return false
  }
  return String(a) === String(b)
}

/**
 * Prefer device `firstLogAt` / `firstTelemetryAt` from API; else earliest `timelineEvents` row
 * for this device id, else earliest for same `dashboardUserId`.
 */
function resolveFirstLogAtForDevice(device, timelineEvents) {
  const fromApi =
    device.firstLogAt ??
    device.FirstLogAt ??
    device.firstTelemetryAt ??
    device.FirstTelemetryAt
  if (fromApi != null && fromApi !== '') {
    return fromApi
  }
  const deviceInstallationId = device.deviceInstallationId ?? device.DeviceInstallationId
  const dashboardUserId = device.dashboardUserId ?? device.DashboardUserId
  let best = null
  for (const ev of timelineEvents || []) {
    const evDev = ev.deviceInstallationId ?? ev.DeviceInstallationId
    const evUid = ev.dashboardUserId ?? ev.DashboardUserId
    const tRaw = ev.occurredAt ?? ev.OccurredAt
    const m = moment(tRaw)
    if (!m.isValid()) {
      continue
    }
    let matches = false
    if (
      deviceInstallationId != null &&
      evDev != null &&
      String(evDev) === String(deviceInstallationId)
    ) {
      matches = true
    } else if (
      (evDev == null || deviceInstallationId == null) &&
      idsEqualNullable(evUid, dashboardUserId)
    ) {
      matches = true
    }
    if (!matches) {
      continue
    }
    if (!best || m.isBefore(best)) {
      best = m
    }
  }
  return best ? best.toISOString() : null
}

function buildTelemetryUsersByIdMap(telemetryUsers) {
  const usersById = new Map()
  for (const u of telemetryUsers || []) {
    const id = u.dashboardUserId ?? u.DashboardUserId
    if (id != null && id !== undefined) {
      usersById.set(String(id), u)
    }
  }
  return usersById
}

/** Prefer api-dashboard roster (full name + email) over telemetry-only stubs. */
function rosterDisplayNameFromDu(du) {
  const n = (du?.displayName ?? du?.DisplayName ?? '').trim()
  if (n) {
    return n
  }
  const fn = (du?.firstName ?? du?.FirstName ?? '').trim()
  const ln = (du?.lastName ?? du?.LastName ?? '').trim()
  if (fn || ln) {
    return `${fn} ${ln}`.trim()
  }
  return ''
}

function getRosterEntryForDashboardUserId(dashboardUserId, dashboardUsers) {
  if (dashboardUserId == null || dashboardUsers == null || !dashboardUsers.length) {
    return null
  }
  const k = String(dashboardUserId)
  const du = dashboardUsers.find(
    r => String(r.dashboardUserId ?? r.DashboardUserId ?? r.userId ?? r.UserId) === k
  )
  if (!du) {
    return null
  }
  const displayName = rosterDisplayNameFromDu(du)
  const email = (du.email ?? du.Email ?? '').trim()
  return { displayName, email }
}

/**
 * Merges `telemetryUsers` with `dashboardUsers` so device/timeline rows show the same names as Users & Roles.
 */
function buildMergedUserProfileByIdMap(telemetryUsers, dashboardUsers) {
  const map = buildTelemetryUsersByIdMap(telemetryUsers)
  for (const du of dashboardUsers || []) {
    const id = du.dashboardUserId ?? du.DashboardUserId ?? du.userId ?? du.UserId
    if (id == null || id === undefined) {
      continue
    }
    const k = String(id)
    const nameFromRoster = rosterDisplayNameFromDu(du)
    const emailFromRoster = (du.email ?? du.Email ?? '').trim()
    const existing = map.get(k) ?? {}
    map.set(k, {
      ...existing,
      dashboardUserId: id,
      displayName:
        nameFromRoster || existing.displayName || existing.DisplayName || `User ${id}`,
      DisplayName: nameFromRoster || existing.DisplayName || existing.displayName,
      email: emailFromRoster || existing.email || existing.Email || '',
      Email: emailFromRoster || existing.Email || existing.email
    })
  }
  return map
}

function buildDeviceInstallRow(d, idx, usersById, timelineEvents) {
  const uid = d.dashboardUserId ?? d.DashboardUserId
  const tu = uid != null && uid !== undefined ? usersById.get(String(uid)) : null
  const unassigned = uid == null || uid === undefined
  return {
    rowKey: `device-${d.deviceInstallationId ?? d.DeviceInstallationId ?? idx}`,
    kind: 'device',
    userName:
      tu?.displayName ??
      tu?.DisplayName ??
      (unassigned ? 'Unassigned installation' : `User ${uid}`),
    email: tu?.email ?? tu?.Email ?? '—',
    dashboardUserId: unassigned ? null : uid,
    deviceId: d.deviceInstallationId ?? d.DeviceInstallationId ?? '—',
    deviceIdentity: formatDeviceIdentity(
      d.osUserName ?? d.OsUserName,
      d.machineName ?? d.MachineName
    ),
    appVersion: d.appVersion ?? d.AppVersion ?? '—',
    firstLogAt: resolveFirstLogAtForDevice(d, timelineEvents),
    lastSeenAt: d.lastSeenAt ?? d.LastSeenAt,
    lastClientEventOccurredAt:
      d.lastClientEventOccurredAt ?? d.LastClientEventOccurredAt ?? null,
    lastClientEventPreview: d.lastClientEventPreview ?? d.LastClientEventPreview ?? null,
    dashboardLoginActive: Boolean(
      d.dashboardLoginActive ?? d.DashboardLoginActive ?? false
    ),
    pepLoginActive: Boolean(d.pepLoginActive ?? d.PepLoginActive ?? false),
    accessibleHotels: parseAccessibleHotels(
      d.accessibleHotelsJson ?? d.AccessibleHotelsJson ?? null
    )
  }
}

function collectDashboardUserIdsFromDevices(devices) {
  const set = new Set()
  for (const d of devices || []) {
    const uid = d.dashboardUserId ?? d.DashboardUserId
    if (uid != null && uid !== undefined) {
      set.add(String(uid))
    }
  }
  return set
}

/**
 * Distinct dashboard user ids with any PEP signal in the payload: devices, timeline events, or
 * activity windows (union). Unassigned (no user id) is not included.
 */
function mergeDashboardUserIdsWithTelemetrySignals(
  userIdsFromDevices,
  timelineEvents,
  activityWindows
) {
  const out = new Set(userIdsFromDevices)
  for (const ev of timelineEvents || []) {
    const uid = ev.dashboardUserId ?? ev.DashboardUserId
    if (uid != null && uid !== undefined) {
      out.add(String(uid))
    }
  }
  for (const w of activityWindows || []) {
    const uid = w.dashboardUserId ?? w.DashboardUserId
    if (uid != null && uid !== undefined) {
      out.add(String(uid))
    }
  }
  return out
}

function earliestSignalAtForUser(uid, timelineEvents, activityWindows) {
  let best = null
  const ks = String(uid)
  for (const ev of timelineEvents || []) {
    const id = ev.dashboardUserId ?? ev.DashboardUserId
    if (id == null || String(id) !== ks) {
      continue
    }
    const m = moment(ev.occurredAt ?? ev.OccurredAt)
    if (!m.isValid()) {
      continue
    }
    if (!best || m.isBefore(best)) {
      best = m
    }
  }
  for (const w of activityWindows || []) {
    const id = w.dashboardUserId ?? w.DashboardUserId
    if (id == null || String(id) !== ks) {
      continue
    }
    const m = moment(w.startAt ?? w.StartAt)
    if (!m.isValid()) {
      continue
    }
    if (!best || m.isBefore(best)) {
      best = m
    }
  }
  return best ? best.toISOString() : null
}

function latestSignalAtForUser(uid, timelineEvents, activityWindows) {
  let best = null
  const ks = String(uid)
  for (const ev of timelineEvents || []) {
    const id = ev.dashboardUserId ?? ev.DashboardUserId
    if (id == null || String(id) !== ks) {
      continue
    }
    const m = moment(ev.occurredAt ?? ev.OccurredAt)
    if (!m.isValid()) {
      continue
    }
    if (!best || m.isAfter(best)) {
      best = m
    }
  }
  for (const w of activityWindows || []) {
    const id = w.dashboardUserId ?? w.DashboardUserId
    if (id == null || String(id) !== ks) {
      continue
    }
    for (const raw of [w.endAt ?? w.EndAt, w.startAt ?? w.StartAt]) {
      const m = moment(raw)
      if (!m.isValid()) {
        continue
      }
      if (!best || m.isAfter(best)) {
        best = m
      }
    }
  }
  return best ? best.toISOString() : null
}

function buildTelemetryOnlyRow(du, uid, usersById, timelineEvents, activityWindows) {
  const k = String(uid)
  const tu = usersById.get(k)
  const userName =
    tu?.displayName ?? tu?.DisplayName ?? rosterDisplayNameFromDu(du) ?? `User ${uid}`
  const email = tu?.email ?? tu?.Email ?? du.email ?? du.Email ?? '—'
  return {
    rowKey: `telemetry-signal-${k}`,
    kind: 'telemetry',
    userName,
    email,
    dashboardUserId: uid,
    deviceId: null,
    appVersion: null,
    firstLogAt: earliestSignalAtForUser(uid, timelineEvents, activityWindows),
    lastSeenAt: latestSignalAtForUser(uid, timelineEvents, activityWindows),
    dashboardLoginActive: false,
    pepLoginActive: false
  }
}

/**
 * Roster-based total; PEP installed = roster members with ≥1 telemetry signal (device, timeline, or
 * activity window) for their dashboard user id in the loaded payload window. Not installed = roster
 * members with none. Legacy payload without roster: uses `expectedUserCount` as total when set.
 */
function computeHotelUserInstallStats(data) {
  const devices = data.devices || []
  const roster = data.dashboardUsers
  let userIdsWithTelemetry = mergeDashboardUserIdsWithTelemetrySignals(
    collectDashboardUserIdsFromDevices(devices),
    data.timelineEvents,
    data.activityWindows
  )
  const internalIds = data.internalGlobalDashboardUserIds || []
  const keepInternalWithDevice = new Set(
    (data.internalIdsWithHotelDevice || []).map(id => String(id))
  )
  if (internalIds.length > 0) {
    const internalSet = new Set(internalIds.map(id => String(id)))
    for (const id of [...userIdsWithTelemetry]) {
      if (internalSet.has(id) && !keepInternalWithDevice.has(id)) {
        userIdsWithTelemetry.delete(id)
      }
    }
  }

  if (roster != null) {
    const rosterIds = roster
      .map(du => du.dashboardUserId ?? du.DashboardUserId ?? du.userId ?? du.UserId)
      .filter(id => id != null && id !== undefined)
    const total = rosterIds.length
    let withApp = 0
    for (const id of rosterIds) {
      if (userIdsWithTelemetry.has(String(id))) {
        withApp += 1
      }
    }
    const withoutApp = total - withApp
    return {
      total,
      withApp,
      withoutApp
    }
  }

  const withApp = userIdsWithTelemetry.size
  const total =
    data.expectedUserCount !== null && data.expectedUserCount !== undefined
      ? data.expectedUserCount
      : null
  const withoutApp = total != null ? Math.max(0, total - withApp) : null
  return {
    total,
    withApp,
    withoutApp
  }
}

function buildNoAppSignalRow(du, uid) {
  const k = String(uid)
  return {
    rowKey: `no-app-signal-${k}`,
    kind: 'missing',
    userName: du.displayName ?? du.DisplayName ?? du.name ?? du.Name ?? `User ${uid}`,
    email: du.email ?? du.Email ?? '—',
    dashboardUserId: uid,
    deviceId: null,
    appVersion: null,
    firstLogAt: null,
    lastSeenAt: null,
    dashboardLoginActive: false,
    pepLoginActive: false
  }
}

/**
 * Users & devices table: `dashboardUsers` is the full hotel roster. Each user gets device rows when
 * the payload lists devices; otherwise a telemetry-only row when timeline/activity has their id;
 * otherwise “no app signal.” Device rows whose `dashboardUserId` is not on the roster are appended
 * after roster rows (e.g. internal or shared dashboard accounts). Rows with PEP desktop signal
 * (device or telemetry) are listed before “No app signal” rows. Without `dashboardUsers`, falls back
 * to legacy devices + missingUsers merge.
 * @param {{ start: moment.Moment, end: moment.Moment }} signalRange Range for device “in range” badges (full loaded payload, not the Timeline picker).
 */
function buildUnifiedUserDeviceRows(data, signalRange) {
  const usersById = buildMergedUserProfileByIdMap(
    data.telemetryUsers,
    mergedRosterProfiles(data.dashboardUsers, data.deviceLinkedUserProfiles)
  )
  const timelineEvents = data.timelineEvents || []
  const activityWindows = data.activityWindows || []
  const devices = data.devices || []
  const dashboardUsers = data.dashboardUsers

  const unassignedDevices = []
  const devicesByUserId = new Map()
  devices.forEach((d, idx) => {
    const uid = d.dashboardUserId ?? d.DashboardUserId
    if (uid == null || uid === undefined) {
      unassignedDevices.push({ d, idx })
    } else {
      const k = String(uid)
      if (!devicesByUserId.has(k)) {
        devicesByUserId.set(k, [])
      }
      devicesByUserId.get(k).push({ d, idx })
    }
  })

  if (dashboardUsers != null) {
    const rows = []
    for (const { d, idx } of unassignedDevices) {
      rows.push(
        attachDeviceInstallSignalFlags(
          buildDeviceInstallRow(d, idx, usersById, timelineEvents),
          signalRange,
          timelineEvents
        )
      )
    }

    const signalUserIds = mergeDashboardUserIdsWithTelemetrySignals(
      collectDashboardUserIdsFromDevices(devices),
      timelineEvents,
      activityWindows
    )

    const rosterIdSet = new Set()
    for (const du of dashboardUsers) {
      const uid = du.dashboardUserId ?? du.DashboardUserId ?? du.userId ?? du.UserId
      if (uid == null || uid === undefined) {
        continue
      }
      const k = String(uid)
      rosterIdSet.add(k)
      const list = devicesByUserId.get(k)
      if (list && list.length > 0) {
        list.forEach(({ d, idx }) => {
          rows.push(
            attachDeviceInstallSignalFlags(
              buildDeviceInstallRow(d, idx, usersById, timelineEvents),
              signalRange,
              timelineEvents
            )
          )
        })
      } else if (signalUserIds.has(k)) {
        rows.push(
          buildTelemetryOnlyRow(du, uid, usersById, timelineEvents, activityWindows)
        )
      } else {
        rows.push(buildNoAppSignalRow(du, uid))
      }
    }

    for (const [deviceOwnerKey, list] of devicesByUserId.entries()) {
      if (rosterIdSet.has(deviceOwnerKey)) {
        continue
      }
      for (const { d, idx } of list) {
        rows.push(
          attachDeviceInstallSignalFlags(
            buildDeviceInstallRow(d, idx, usersById, timelineEvents),
            signalRange,
            timelineEvents
          )
        )
      }
    }

    rows.sort((a, b) => {
      const rank = r => (r.kind === 'missing' ? 1 : 0)
      return rank(a) - rank(b)
    })

    return rows
  }

  const deviceRows = devices.map((d, idx) =>
    attachDeviceInstallSignalFlags(
      buildDeviceInstallRow(d, idx, usersById, timelineEvents),
      signalRange,
      timelineEvents
    )
  )

  const missingRows = (data.missingUsers || []).map((m, idx) => ({
    rowKey: `missing-${m.email ?? m.Email ?? idx}`,
    kind: 'missing',
    userName: m.name ?? m.Name ?? '—',
    email: m.email ?? m.Email ?? '—',
    dashboardUserId: m.userId ?? m.UserId ?? null,
    deviceId: null,
    appVersion: null,
    firstLogAt: null,
    lastSeenAt: null,
    dashboardLoginActive: false,
    pepLoginActive: false
  }))

  return [...deviceRows, ...missingRows]
}

function LoginStatusBadge({ active, stale, label }) {
  const resolvedState = stale ? 'unknown' : active ? 'active' : 'inactive'
  const className =
    resolvedState === 'active'
      ? styles.loginStatusOn
      : resolvedState === 'inactive'
      ? styles.loginStatusOff
      : styles.loginStatusUnknown
  const text =
    resolvedState === 'active'
      ? 'Active'
      : resolvedState === 'inactive'
      ? 'Inactive'
      : 'Unknown'
  const title = stale
    ? `${label}. Last known value is stale because this device has no telemetry in the selected timeline window.`
    : label
  return (
    <span className={className} title={title} role="status">
      {text}
    </span>
  )
}

function isLoginStatusStaleForRow(row) {
  if (row?.kind !== 'device') {
    return false
  }
  if (row.deviceSignalInSelectedRange === false) {
    return true
  }
  const lastSeen = moment(row.lastSeenAt)
  if (!lastSeen.isValid()) {
    return true
  }
  return moment().diff(lastSeen, 'hours', true) >= 24
}

function PepInstallStatusBadge({ kind, deviceSignalInSelectedRange }) {
  if (kind === 'missing') {
    return (
      <div className={styles.userCellBadgeRow}>
        <span
          className={styles.pepInstallBadgeNoSignal}
          title="No registered PEP desktop device and no telemetry for this user in the loaded telemetry window"
          role="status"
        >
          No app signal
        </span>
      </div>
    )
  }
  if (kind === 'device') {
    const stale = deviceSignalInSelectedRange === false
    return (
      <div className={styles.userCellBadgeRow}>
        <span
          className={
            stale ? styles.pepInstallBadgeDeviceStale : styles.pepInstallBadgeDesktop
          }
          title={
            stale
              ? 'Device is saved; no telemetry in the loaded window (PC may be off or offline)'
              : 'PEP desktop device registered; telemetry in the loaded window'
          }
          role="status"
        >
          {stale ? 'PEP desktop · Not in range' : 'PEP desktop'}
        </span>
      </div>
    )
  }
  if (kind === 'telemetry') {
    return (
      <div className={styles.userCellBadgeRow}>
        <span
          className={styles.pepInstallBadgeDesktop}
          title="PEP desktop telemetry for this user in the loaded window (no device row)"
          role="status"
        >
          PEP desktop
        </span>
      </div>
    )
  }
  return null
}

const ElectronTelemetryModal = ({
  isOpen,
  onClose,
  subsidiaryId,
  hotelDisplayId,
  hotelLabel,
  openedFromPmsHealthCheckDetails = false,
  csmUserFullName,
  lastResult,
  pmsSyncLastInfoReceivedOn,
  pmsSyncTotalReportsReceivedInLast48Hours,
  isHavingProblems,
  pmsSyncIsConnected,
  isDisabled,
  pepDesktopAppDeviceCount,
  pepDesktopAppDistinctUserCount,
  pepDashboardUserCount,
  pepCredentialIssueKind = '',
  pepCredentialLastFailureReason = '',
  pepCredentialWorkingCount = 0,
  pepCredentialWrongPasswordCount = 0,
  pepCredentialChangePasswordCount = 0,
  pepPasswordExpireDays = null
}) => {
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(null)
  const [data, setData] = useState(() => normalizePayload(null))
  const [timelineUserFilter, setTimelineUserFilter] = useState('all')
  const [selectedTimelineEvent, setSelectedTimelineEvent] = useState(null)
  const [selectedActivityWindow, setSelectedActivityWindow] = useState(null)
  const [telemetryFromDate, setTelemetryFromDate] = useState(() =>
    moment().format('YYYY-MM-DD')
  )
  const [telemetryToDate, setTelemetryToDate] = useState(() =>
    moment().format('YYYY-MM-DD')
  )
  const [eventLogRowLimit, setEventLogRowLimit] = useState(EventLogDefaultDisplayRows)
  const [showSituationDetails, setShowSituationDetails] = useState(false)

  const [pmsUploadHistoriesLoading, setPmsUploadHistoriesLoading] = useState(false)
  const [pmsUploadHistoriesError, setPmsUploadHistoriesError] = useState(null)
  const [pmsUploadHistoriesPage, setPmsUploadHistoriesPage] = useState(1)
  const [pmsHistoriesFilterMonth, setPmsHistoriesFilterMonth] = useState(
    () => moment().month() + 1
  )
  const [pmsHistoriesFilterYear, setPmsHistoriesFilterYear] = useState(() =>
    moment().year()
  )
  const [pmsUploadHistoriesRaw, setPmsUploadHistoriesRaw] = useState(null)
  const [pmsUploadHistoriesActiveTabName, setPmsUploadHistoriesActiveTabName] =
    useState(null)

  const [diagnosticCapturesLoading, setDiagnosticCapturesLoading] = useState(false)
  const [diagnosticCapturesError, setDiagnosticCapturesError] = useState(null)
  const [diagnosticCapturesRaw, setDiagnosticCapturesRaw] = useState(null)
  const [expandedCaptureIds, setExpandedCaptureIds] = useState(() => new Set())
  const [selectedCaptureScreenshot, setSelectedCaptureScreenshot] = useState(null)
  const [selectedCaptureHtml, setSelectedCaptureHtml] = useState(null)
  const [selectedCaptureHarText, setSelectedCaptureHarText] = useState(null)
  const [credentialCaptureNotice, setCredentialCaptureNotice] = useState(null)
  const [selectedAccessibleHotels, setSelectedAccessibleHotels] = useState(null)
  const [diagnosticCapturesPage, setDiagnosticCapturesPage] = useState(1)

  const hotelTimeZoneId = data.hotelTimeZoneId

  const timelineRange = useMemo(
    () =>
      buildTimelineViewRangeFromCalendar(
        telemetryFromDate,
        telemetryToDate,
        hotelTimeZoneId
      ),
    [telemetryFromDate, telemetryToDate, hotelTimeZoneId]
  )

  const eventsInView = useMemo(() => {
    return (data.timelineEvents || []).filter(ev =>
      eventInTimelineView(ev, timelineRange.start, timelineRange.end)
    )
  }, [data.timelineEvents, timelineRange])

  const activityWindowsInView = useMemo(() => {
    return (data.activityWindows || []).filter(w =>
      activityWindowOverlapsTimeline(w, timelineRange.start, timelineRange.end)
    )
  }, [data.activityWindows, timelineRange])

  const fullPayloadSignalRange = useMemo(() => buildFullPayloadSignalRange(data), [data])

  const formatTime = value => formatHotelTime(value, hotelTimeZoneId)

  const hotelTimeZoneLabel = useMemo(
    () => getHotelTimeZoneLabel(hotelTimeZoneId),
    [hotelTimeZoneId]
  )

  const hotelTimeZoneResolved = resolveHotelTimeZone(hotelTimeZoneId)

  const hotelNow = useMemo(() => getHotelNow(hotelTimeZoneId), [hotelTimeZoneId])

  const diagnosticCaptures = useMemo(
    () =>
      normalizeDiagnosticCaptures(diagnosticCapturesRaw, {
        internalGlobalDashboardUserIds: data.internalGlobalDashboardUserIds
      }),
    [diagnosticCapturesRaw, data.internalGlobalDashboardUserIds]
  )

  const diagnosticCapturesPageCount = Math.max(
    1,
    Math.ceil(diagnosticCaptures.length / DIAGNOSTIC_CAPTURES_PAGE_SIZE)
  )

  // Keep the requested page within bounds when the capture list changes (reload, filter).
  useEffect(() => {
    setDiagnosticCapturesPage(prev => Math.min(Math.max(prev, 1), diagnosticCapturesPageCount))
  }, [diagnosticCapturesPageCount])

  const pagedDiagnosticCaptures = useMemo(() => {
    const start = (diagnosticCapturesPage - 1) * DIAGNOSTIC_CAPTURES_PAGE_SIZE
    return diagnosticCaptures.slice(start, start + DIAGNOSTIC_CAPTURES_PAGE_SIZE)
  }, [diagnosticCaptures, diagnosticCapturesPage])

  const openCredentialReasonCaptures = credentialRow => {
    const credentialIssue = classifyPepCredentialFailureReason(
      credentialRow?.lastFailureReason
    )
    const { matches, emptyReason } = matchCredentialDiagnosticCaptures(
      diagnosticCaptures,
      credentialRow,
      credentialIssue
    )
    const firstShot = matches.find(c => c.hasScreenshot && c.screenshotUrl)
    if (firstShot?.screenshotUrl) {
      setCredentialCaptureNotice(null)
      setSelectedCaptureScreenshot(firstShot.screenshotUrl)
      return
    }
    setSelectedCaptureScreenshot(null)
    setCredentialCaptureNotice(emptyReason)
  }

  useEffect(() => {
    if (!isOpen || !subsidiaryId) {
      return undefined
    }

    const todayStr = moment().format('YYYY-MM-DD')
    // Fetch window is UTC-anchored so every viewer pulls the same dataset (a viewer-local window
    // shifted by a day made teammates in different timezones see different log counts). The client
    // date picker keeps the viewer-local "today" label; the default view stays the shared last-24h.
    const { startDate: loadStart, endDate: loadEnd } = buildElectronTelemetryFetchWindow(
      ELECTRON_TELEMETRY_FETCH_LOOKBACK_DAYS
    )

    setTelemetryFromDate(todayStr)
    setTelemetryToDate(todayStr)

    let cancelled = false
    setLoading(true)
    setLoadError(null)
    setTimelineUserFilter('all')
    setSelectedTimelineEvent(null)
    setSelectedActivityWindow(null)
    setEventLogRowLimit(EventLogDefaultDisplayRows)
    setShowSituationDetails(false)

    const runLoad = () =>
      internalOnlyApi.getElectronTelemetrySummary({
        subsidiaryId,
        startDate: loadStart,
        endDate: loadEnd
      })

    runLoad()
      .then(result => {
        if (!cancelled) {
          const normalized = normalizePayload(result)
          const hotelTodayStr = getHotelTodayDateString(
            normalized.hotelTimeZoneId ?? normalized.HotelTimeZoneId ?? null
          )
          let calendarFrom = hotelTodayStr
          let calendarTo = hotelTodayStr
          let userFilter = 'all'
          let initialLogLimit = EventLogDefaultDisplayRows

          const focus = resolveTimelineOpenFocus(normalized, hotelTodayStr, {
            spanFromLastSignalDay: openedFromPmsHealthCheckDetails
          })
          calendarFrom = focus.fromStr
          calendarTo = focus.toStr
          userFilter = focus.userFilter
          initialLogLimit = focus.eventLogRowLimit

          setTelemetryFromDate(calendarFrom)
          setTelemetryToDate(calendarTo)
          setData(normalized)
          setTimelineUserFilter(userFilter)
          setSelectedTimelineEvent(null)
          setSelectedActivityWindow(null)
          setEventLogRowLimit(initialLogLimit)
        }
      })
      .catch(err => {
        if (!cancelled) {
          setLoadError(
            err?.message ||
              'Unable to load desktop telemetry. The API may not be deployed yet for this environment.'
          )
          setData(normalizePayload(null))
          setTimelineUserFilter('all')
          setSelectedTimelineEvent(null)
          setSelectedActivityWindow(null)
          setEventLogRowLimit(EventLogDefaultDisplayRows)
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [isOpen, subsidiaryId, openedFromPmsHealthCheckDetails])

  useEffect(() => {
    if (!isOpen) {
      return undefined
    }
    if (openedFromPmsHealthCheckDetails) {
      if (subsidiaryId == null || String(subsidiaryId).trim() === '') {
        setPmsUploadHistoriesLoading(false)
        setPmsUploadHistoriesError('Upload history requires a hotel id for this view.')
        setPmsUploadHistoriesRaw(null)
        setPmsUploadHistoriesActiveTabName(null)
        return undefined
      }
    }
    let cancelled = false
    const month = pmsHistoriesFilterMonth
    const year = pmsHistoriesFilterYear
    setPmsUploadHistoriesLoading(true)
    setPmsUploadHistoriesError(null)
    setPmsUploadHistoriesPage(1)
    setPmsUploadHistoriesRaw(null)
    const historiesPromise =
      openedFromPmsHealthCheckDetails && subsidiaryId
        ? internalOnlyApi.getPmsHistoriesForHealthCheck({ subsidiaryId, month, year })
        : pmsApi.histories({ month, year })
    historiesPromise
      .then(res => {
        if (!cancelled) {
          const logs = res?.logs ?? res?.Logs ?? []
          const tabNames = [...new Set(logs.map(l => l.name ?? l.Name).filter(Boolean))]
          setPmsUploadHistoriesRaw(res)
          setPmsUploadHistoriesActiveTabName(prev =>
            prev && tabNames.includes(prev) ? prev : tabNames[0] ?? null
          )
        }
      })
      .catch(err => {
        if (!cancelled) {
          setPmsUploadHistoriesError(err?.message || 'Unable to load upload logs.')
          setPmsUploadHistoriesRaw(null)
          setPmsUploadHistoriesActiveTabName(null)
        }
      })
      .finally(() => {
        if (!cancelled) {
          setPmsUploadHistoriesLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [
    isOpen,
    pmsHistoriesFilterMonth,
    pmsHistoriesFilterYear,
    openedFromPmsHealthCheckDetails,
    subsidiaryId
  ])

  useEffect(() => {
    if (!isOpen || !subsidiaryId) {
      return undefined
    }

    // Same UTC-anchored window as the telemetry summary: the api resolves these calendar-day
    // strings against the hotel timezone, so a viewer-local window dropped a day of captures.
    const { startDate: loadStart, endDate: loadEnd } = buildElectronTelemetryFetchWindow(
      ELECTRON_TELEMETRY_FETCH_LOOKBACK_DAYS
    )

    let cancelled = false
    setDiagnosticCapturesLoading(true)
    setDiagnosticCapturesError(null)
    setDiagnosticCapturesRaw(null)
    setExpandedCaptureIds(new Set())
    setSelectedCaptureScreenshot(null)
    setSelectedCaptureHtml(null)
    setSelectedCaptureHarText(null)
    setCredentialCaptureNotice(null)
    setDiagnosticCapturesPage(1)

    internalOnlyApi
      .getElectronDiagnosticCaptures({
        subsidiaryId,
        startDate: loadStart,
        endDate: loadEnd
      })
      .then(result => {
        if (!cancelled) {
          setDiagnosticCapturesRaw(result)
        }
      })
      .catch(err => {
        if (!cancelled) {
          setDiagnosticCapturesError(
            err?.message || 'Unable to load diagnostic captures for this hotel.'
          )
          setDiagnosticCapturesRaw(null)
        }
      })
      .finally(() => {
        if (!cancelled) {
          setDiagnosticCapturesLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [isOpen, subsidiaryId])

  useEffect(() => {
    setPmsUploadHistoriesPage(1)
  }, [pmsUploadHistoriesActiveTabName])

  const title = useMemo(() => {
    const label = (hotelLabel || subsidiaryId || '').trim()
    const id =
      hotelDisplayId != null && String(hotelDisplayId).trim() !== ''
        ? String(hotelDisplayId).trim()
        : null
    if (id) {
      return label ? `#${id} — ${label}` : `#${id}`
    }
    return label || 'Electron telemetry'
  }, [hotelDisplayId, hotelLabel, subsidiaryId])

  const latestSignalInPayload = useMemo(() => {
    if (!openedFromPmsHealthCheckDetails) {
      return null
    }
    return resolveLatestSignalMomentFromPayload(data)
  }, [openedFromPmsHealthCheckDetails, data])

  /** Hide the Health Check gap banner when the latest signal is fresh (within 24 hours). */
  const showHealthCheckStaleSignalInsight = useMemo(() => {
    if (!openedFromPmsHealthCheckDetails) {
      return false
    }
    if (!latestSignalInPayload?.isValid()) {
      return true
    }
    const hoursSince = moment().diff(latestSignalInPayload, 'hours', true)
    return hoursSince >= 24
  }, [openedFromPmsHealthCheckDetails, latestSignalInPayload])

  const rosterForDisplayNames = useMemo(
    () => mergedRosterProfiles(data.dashboardUsers, data.deviceLinkedUserProfiles),
    [data.dashboardUsers, data.deviceLinkedUserProfiles]
  )

  const telemetryUsersResolved = useMemo(
    () => resolveTelemetryUsersForPayload(data),
    [
      data.telemetryUsers,
      data.devices,
      data.timelineEvents,
      data.dashboardUsers,
      data.deviceLinkedUserProfiles,
      data.internalGlobalDashboardUserIds
    ]
  )

  const selectedUserFromFilter = useMemo(() => {
    if (timelineUserFilter === 'all') {
      return null
    }
    const fromTelemetry = telemetryUsersResolved.find(
      u => timelineUserKeyFromTelemetryUser(u) === timelineUserFilter
    )
    if (fromTelemetry) {
      return fromTelemetry
    }
    const roster = getRosterEntryForDashboardUserId(
      timelineUserFilter,
      rosterForDisplayNames
    )
    if (!roster) {
      return null
    }
    const id = Number(timelineUserFilter)
    const displayName =
      roster.displayName && String(roster.displayName).trim() !== ''
        ? roster.displayName
        : null
    return {
      dashboardUserId: Number.isNaN(id) ? timelineUserFilter : id,
      displayName,
      DisplayName: displayName,
      email: roster.email,
      Email: roster.email
    }
  }, [timelineUserFilter, telemetryUsersResolved, rosterForDisplayNames])

  const devicesForTimelineUser = useMemo(() => {
    if (timelineUserFilter === 'all') {
      return []
    }
    return data.devices.filter(d => {
      const id = d.dashboardUserId ?? d.DashboardUserId
      if (timelineUserFilter === 'unassigned') {
        return id == null || id === undefined
      }
      return String(id) === timelineUserFilter
    })
  }, [data.devices, timelineUserFilter])

  function isSameTimelineEvent(a, b) {
    if (!a || !b) {
      return false
    }
    const ida = a.id ?? a.Id
    const idb = b.id ?? b.Id
    if (ida != null && idb != null) {
      return ida === idb
    }
    return (
      (a.occurredAt ?? a.OccurredAt) === (b.occurredAt ?? b.OccurredAt) &&
      (a.eventType ?? a.EventType) === (b.eventType ?? b.EventType)
    )
  }

  const deviceIdentityByInstallationId = useMemo(() => {
    const map = new Map()
    for (const d of data.devices || []) {
      const did = d.deviceInstallationId ?? d.DeviceInstallationId
      if (did == null || did === '') {
        continue
      }
      map.set(String(did), {
        osUserName: d.osUserName ?? d.OsUserName ?? null,
        machineName: d.machineName ?? d.MachineName ?? null
      })
    }
    return map
  }, [data.devices])

  // Per-log-line machine identity ("OsUser @ Machine") so the same dashboard user across 2-3
  // machines can be told apart; falls back to the device installation id when identity is unknown.
  function machineIdentityForEvent(ev) {
    const did = ev.deviceInstallationId ?? ev.DeviceInstallationId
    if (did == null || did === '') {
      return null
    }
    const entry = deviceIdentityByInstallationId.get(String(did))
    const identity = entry
      ? formatDeviceIdentity(entry.osUserName, entry.machineName)
      : null
    return identity ?? formatDeviceInstallationIdForTable(did)
  }

  const visibleUsersForSwimlanes = useMemo(() => {
    if (timelineUserFilter === 'all') {
      return telemetryUsersResolved
    }
    return telemetryUsersResolved.filter(
      u => timelineUserKeyFromTelemetryUser(u) === timelineUserFilter
    )
  }, [timelineUserFilter, telemetryUsersResolved])

  const hasTimelineContent =
    eventsInView.length > 0 ||
    (activityWindowsInView && activityWindowsInView.length > 0) ||
    telemetryUsersResolved.length > 0

  const unifiedUserDeviceRows = useMemo(
    () => buildUnifiedUserDeviceRows(data, fullPayloadSignalRange),
    [data, fullPayloadSignalRange]
  )

  const hotelUserInstallSummary = useMemo(
    () => computeHotelUserInstallStats(data),
    [data]
  )

  const timelineLegendActiveReportingColor = useMemo(() => {
    if (timelineUserFilter === 'all') {
      return null
    }
    const u = selectedUserFromFilter
    if (!u) {
      return null
    }
    const key = timelineUserKeyFromTelemetryUser(u)
    const idx = telemetryUsersResolved.indexOf(u)
    return markerColorForUserKey(key, idx >= 0 ? idx : 0)
  }, [timelineUserFilter, selectedUserFromFilter, telemetryUsersResolved])

  const displayActivityWindowsInView = useMemo(() => {
    const gapMinutes = data.gapThresholdMinutes ?? 15
    const keys = new Set()
    for (const u of telemetryUsersResolved) {
      keys.add(timelineUserKeyFromTelemetryUser(u))
    }
    for (const w of activityWindowsInView) {
      keys.add(activityWindowUserKey(w))
    }
    const merged = []
    for (const userKey of keys) {
      const apiWins = windowsForUserKey(activityWindowsInView, userKey)
      merged.push(
        ...resolveDisplayActivityWindowsForUser(
          userKey,
          apiWins,
          eventsInView,
          activityWindowsInView,
          gapMinutes
        )
      )
    }
    return merged
  }, [
    telemetryUsersResolved,
    activityWindowsInView,
    eventsInView,
    data.gapThresholdMinutes
  ])

  const eventLogEventsLoaded = useMemo(() => {
    const buildRawItems = () => {
      if (timelineUserFilter !== 'all') {
        return buildUserActivityLogItems(
          timelineUserFilter,
          eventsInView,
          displayActivityWindowsInView,
          timelineRange.start,
          timelineRange.end
        ).filter(item => item.kind === 'event')
      }
      if (!selectedActivityWindow) {
        return []
      }
      const wk = activityWindowUserKey(selectedActivityWindow)
      const ws = moment(selectedActivityWindow.startAt ?? selectedActivityWindow.StartAt)
      const we = moment(selectedActivityWindow.endAt ?? selectedActivityWindow.EndAt)
      if (!ws.isValid() || !we.isValid()) {
        return []
      }
      const items = []
      for (const ev of eventsInView) {
        const t = moment(ev.occurredAt ?? ev.OccurredAt)
        if (!t.isValid() || !t.isBetween(ws, we, null, '[]')) {
          continue
        }
        if (
          !eventMatchesTimelineFilterWithSessionAttribution(
            ev,
            wk,
            displayActivityWindowsInView
          )
        ) {
          continue
        }
        items.push({ kind: 'event', data: ev })
      }
      items.sort((a, b) => {
        const ta = moment(a.data.occurredAt ?? a.data.OccurredAt).valueOf()
        const tb = moment(b.data.occurredAt ?? b.data.OccurredAt).valueOf()
        return tb - ta
      })
      return items
    }
    const visibleItems = buildRawItems().filter(item => !isLowValueInfraEvent(item.data))
    const groupedItems = groupTelemetryRows(visibleItems, timelineRowSignature)
    return groupedItems.slice(0, EventLogMaxLoadedRows)
  }, [
    timelineUserFilter,
    selectedActivityWindow,
    eventsInView,
    displayActivityWindowsInView,
    timelineRange.start,
    timelineRange.end
  ])

  const eventLogTableEvents = useMemo(() => {
    const cap = Math.min(EventLogMaxLoadedRows, eventLogRowLimit)
    return eventLogEventsLoaded.slice(0, cap)
  }, [eventLogEventsLoaded, eventLogRowLimit])

  const showTimelineDetailZone =
    timelineUserFilter !== 'all' || selectedActivityWindow != null

  const showCsmNoPepInstallAlert =
    hotelUserInstallSummary.total > 0 &&
    hotelUserInstallSummary.withApp === 0 &&
    (data.devices?.length ?? 0) === 0

  const pepHealthCheckDisplay = useMemo(
    () =>
      openedFromPmsHealthCheckDetails
        ? getPepHealthCheckDisplay(lastResult, 'pep', 0)
        : null,
    [openedFromPmsHealthCheckDetails, lastResult]
  )

  const hotelTelemetrySituation = useMemo(
    () =>
      deriveHotelTelemetrySituation({
        devices: data.devices,
        timelineEvents: data.timelineEvents,
        pepHealthCheckDisplay,
        lastResult,
        hotelTimeZoneId
      }),
    [data.devices, data.timelineEvents, pepHealthCheckDisplay, lastResult, hotelTimeZoneId]
  )

  const pepDataRhythm = useMemo(
    () =>
      derivePepDataRhythm({
        timelineEvents: data.timelineEvents,
        lastActivityAt:
          data.summary?.lastActivityAt ?? data.summary?.LastActivityAt ?? null,
        pepHealthCheckDisplay,
        hotelTimeZoneId,
        now: hotelNow
      }),
    [data.timelineEvents, data.summary, pepHealthCheckDisplay, hotelTimeZoneId, hotelNow]
  )

  const pepDataRhythmInfoBar = useMemo(
    () => buildPepDataRhythmInfoBar(pepDataRhythm),
    [pepDataRhythm]
  )

  const pmsSyncLastInfoReceivedOnInstant = useMemo(
    () => hotelLocalWallClockToUtcInstant(pmsSyncLastInfoReceivedOn, hotelTimeZoneId),
    [pmsSyncLastInfoReceivedOn, hotelTimeZoneId]
  )

  const listLevelCsmAction = useMemo(() => {
    if (!openedFromPmsHealthCheckDetails) {
      return null
    }
    return derivePepCsmAction({
      pms: 'pep',
      lastResult,
      pmsSyncLastInfoReceivedOn: pmsSyncLastInfoReceivedOnInstant,
      pmsSyncTotalReportsReceivedInLast48Hours,
      isHavingProblems,
      pmsSyncIsConnected,
      isDisabled,
      pepDesktopAppDeviceCount,
      pepDesktopAppDistinctUserCount,
      pepDashboardUserCount,
      pepCredentialIssueKind,
      pepCredentialLastFailureReason,
      pepCredentialWorkingCount,
      pepCredentialWrongPasswordCount,
      pepCredentialChangePasswordCount,
      pepPasswordExpireDays
    })
  }, [
    openedFromPmsHealthCheckDetails,
    lastResult,
    pmsSyncLastInfoReceivedOnInstant,
    pmsSyncTotalReportsReceivedInLast48Hours,
    isHavingProblems,
    pmsSyncIsConnected,
    isDisabled,
    pepDesktopAppDeviceCount,
    pepDesktopAppDistinctUserCount,
    pepDashboardUserCount,
    pepCredentialIssueKind,
    pepCredentialLastFailureReason,
    pepCredentialWorkingCount,
    pepCredentialWrongPasswordCount,
    pepCredentialChangePasswordCount,
    pepPasswordExpireDays
  ])

  const pepLoginLadder = useMemo(
    () =>
      buildPepLoginLadder({
        devices: data.devices,
        timelineEvents: data.timelineEvents
      }),
    [data.devices, data.timelineEvents]
  )

  const uploadLogMonthLabel = useMemo(
    () =>
      moment({
        year: pmsHistoriesFilterYear,
        month: pmsHistoriesFilterMonth - 1
      }).format('MMMM YYYY'),
    [pmsHistoriesFilterMonth, pmsHistoriesFilterYear]
  )

  const pmsUploadHistoriesMonthDropdownOptions = useMemo(() => {
    const y = pmsHistoriesFilterYear
    const nowY = moment().year()
    if (y === nowY) {
      return Array(moment().month() + 1)
        .fill(0)
        .map((_, i) => moment().subtract(i, 'months').month() + 1)
        .reduce((acc, m) => ({ ...acc, [m]: PMS_UPLOAD_HISTORY_MONTH_NAMES[m - 1] }), {})
    }
    return PMS_UPLOAD_HISTORY_MONTH_NAMES.reduce(
      (acc, name, i) => ({ ...acc, [i + 1]: name }),
      {}
    )
  }, [pmsHistoriesFilterYear])

  const pmsUploadHistoriesYearDropdownOptions = useMemo(
    () =>
      Array(8)
        .fill(0)
        .map((_, i) => moment().subtract(i, 'years').year())
        .reduce((acc, y) => ({ ...acc, [y]: `${y}` }), {}),
    []
  )

  const pmsUploadHistoriesTabListItems = useMemo(() => {
    const logs = pmsUploadHistoriesRaw?.logs ?? pmsUploadHistoriesRaw?.Logs ?? []
    const keys = [...new Set(logs.map(l => l.name ?? l.Name).filter(Boolean))]
    return keys.map(tabKey => {
      const grp = logs.find(l => (l.name ?? l.Name) === tabKey)
      const tabLabel =
        grp?.info?.name ?? grp?.Info?.Name ?? grp?.name ?? grp?.Name ?? tabKey
      return { tabKey, tabLabel }
    })
  }, [pmsUploadHistoriesRaw])

  const pmsUploadHistoriesActiveDayGroups = useMemo(() => {
    const logs = pmsUploadHistoriesRaw?.logs ?? pmsUploadHistoriesRaw?.Logs ?? []
    if (!pmsUploadHistoriesActiveTabName) {
      return []
    }
    const group = logs.find(l => (l.name ?? l.Name) === pmsUploadHistoriesActiveTabName)
    const dayList = group?.logs ?? group?.Logs ?? []
    return Array.isArray(dayList) ? dayList : []
  }, [pmsUploadHistoriesRaw, pmsUploadHistoriesActiveTabName])

  const pmsUploadHistoriesPager = useMemo(() => {
    const sorted = [...pmsUploadHistoriesActiveDayGroups].sort((a, b) => {
      const da = moment(a?.date ?? a?.Date)
      const db = moment(b?.date ?? b?.Date)
      if (!da.isValid() && !db.isValid()) {
        return 0
      }
      if (!da.isValid()) {
        return 1
      }
      if (!db.isValid()) {
        return -1
      }
      return db.valueOf() - da.valueOf()
    })
    const total = sorted.length
    const totalPages = total === 0 ? 0 : Math.ceil(total / PMS_UPLOAD_HISTORY_PAGE_SIZE)
    const page =
      totalPages === 0 ? 1 : Math.min(Math.max(1, pmsUploadHistoriesPage), totalPages)
    const start = (page - 1) * PMS_UPLOAD_HISTORY_PAGE_SIZE
    const slice = sorted.slice(start, start + PMS_UPLOAD_HISTORY_PAGE_SIZE)
    const rangeStart = total === 0 ? 0 : start + 1
    const rangeEnd =
      total === 0 ? 0 : Math.min(start + PMS_UPLOAD_HISTORY_PAGE_SIZE, total)
    return { total, totalPages, page, slice, rangeStart, rangeEnd }
  }, [pmsUploadHistoriesActiveDayGroups, pmsUploadHistoriesPage])

  if (!isOpen) {
    return null
  }

  const csmDisplayName =
    csmUserFullName != null && String(csmUserFullName).trim() !== ''
      ? String(csmUserFullName).trim()
      : 'Not assigned'
  const guestDataUploadsHelpText = openedFromPmsHealthCheckDetails
    ? `${HELP_GUEST_DATA_UPLOADS_BASE} Uploads are loaded for the hotel on this Health Check row (not the dashboard header hotel).`
    : `${HELP_GUEST_DATA_UPLOADS_BASE} The API uses the hotel selected in the dashboard header, which may differ from this modal title.`

  return (
    <ConfirmationDialog
      title={title}
      headerTrailing={
        <div className={styles.telemetryModalHeaderCsm}>
          <div className={styles.telemetryModalFooterCsm}>
            <span className={styles.telemetryModalFooterCsmLabel}>CSM</span>
            <span className={styles.telemetryModalFooterCsmName} title={csmDisplayName}>
              {csmDisplayName}
            </span>
          </div>
        </div>
      }
      onClose={onClose}
      hasConfirmButton={false}
      maxWidth={1160}
      contentPadding="12px 18px 20px"
      bottomBarDetailFullWidth
      bottomBarDetail={
        <div className={styles.telemetryModalFooterActionsOnly}>
          <Link to={`/s/${subsidiaryId}/hotelDetails`} onClick={onClose}>
            <PrimarySmallButton type="button">Go to hotel details →</PrimarySmallButton>
          </Link>
        </div>
      }
    >
      <div className={styles.root}>
        {loading && <LoadingIndicator marginTop={24} />}

        {!loading && loadError && !isLikelyAuthorizationDeniedMessage(loadError) && (
          <div className={styles.errorBanner}>{loadError}</div>
        )}

        {!loading && !loadError && (
          <>
            {!hotelTimeZoneResolved && (
              <div className={styles.timezoneWarningBanner} role="status">
                Hotel time zone is not configured. Times are shown in your browser&apos;s local
                time until the hotel profile includes a valid time zone.
              </div>
            )}
            <section
              className={[
                styles.currentSituationPanel,
                hotelTelemetrySituation.severity === 'error'
                  ? styles.currentSituationPanelError
                  : hotelTelemetrySituation.severity === 'warn'
                  ? styles.currentSituationPanelWarn
                  : styles.currentSituationPanelInfo
              ].join(' ')}
              aria-labelledby="electron-telemetry-current-situation"
            >
              <h3 id="electron-telemetry-current-situation" className={styles.currentSituationTitle}>
                What to do
              </h3>
              {listLevelCsmAction ? (
                <>
                  <p className={styles.currentSituationActionLabel}>{listLevelCsmAction.actionLabel}</p>
                  <p className={styles.currentSituationAction}>{listLevelCsmAction.actionInstruction}</p>
                  <p className={styles.currentSituationHeadlineMuted}>
                    {hotelTelemetrySituation.headline}
                  </p>
                </>
              ) : (
                <>
                  <p className={styles.currentSituationHeadline}>{hotelTelemetrySituation.headline}</p>
                  <p className={styles.currentSituationAction}>
                    <strong>Recommended action:</strong> {hotelTelemetrySituation.recommendedAction}
                  </p>
                </>
              )}
              {(pepLoginLadder.length > 0 ||
                hotelTelemetrySituation.bullets.length > 0 ||
                hotelTelemetrySituation.sparseTimelineNote) && (
                <button
                  type="button"
                  className={styles.currentSituationDetailsToggle}
                  onClick={() => setShowSituationDetails(open => !open)}
                >
                  {showSituationDetails ? 'Hide technical details' : 'Show technical details'}
                </button>
              )}
              {showSituationDetails && pepLoginLadder.length > 0 && (
                <ol className={styles.pepLoginLadder} aria-label="PEP login progress">
                  {pepLoginLadder.map(step => (
                    <li
                      key={step.key}
                      className={[
                        styles.pepLoginLadderStep,
                        step.status === PEP_LADDER_STATUS.OK
                          ? styles.pepLoginLadderStepOk
                          : step.status === PEP_LADDER_STATUS.FAIL
                          ? styles.pepLoginLadderStepFail
                          : styles.pepLoginLadderStepUnknown
                      ].join(' ')}
                    >
                      <span className={styles.pepLoginLadderDot} aria-hidden="true" />
                      <span className={styles.pepLoginLadderLabel}>{step.label}</span>
                      <span className={styles.pepLoginLadderState}>
                        {step.status === PEP_LADDER_STATUS.OK
                          ? 'OK'
                          : step.status === PEP_LADDER_STATUS.FAIL
                          ? 'Failed'
                          : 'Unknown'}
                      </span>
                      {step.detail && (
                        <span className={styles.pepLoginLadderDetail}>{step.detail}</span>
                      )}
                      {step.at && (
                        <span className={styles.pepLoginLadderTime}>{formatTime(step.at)}</span>
                      )}
                    </li>
                  ))}
                </ol>
              )}
              {showSituationDetails && hotelTelemetrySituation.bullets.length > 0 && (
                <ul className={styles.currentSituationList}>
                  {hotelTelemetrySituation.bullets.map(item => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              )}
              {showSituationDetails && hotelTelemetrySituation.sparseTimelineNote && (
                <p className={styles.currentSituationNote}>{hotelTelemetrySituation.sparseTimelineNote}</p>
              )}
            </section>

            <section
              className={styles.section}
              aria-labelledby="electron-telemetry-summary"
            >
              <h3 id="electron-telemetry-summary" className={styles.sectionTitle}>
                Summary
                <SectionInfoButton text={HELP_SUMMARY} />
              </h3>
              {showCsmNoPepInstallAlert && (
                <div className={styles.csmAlertBanner} role="alert">
                  <strong>Alert:</strong> No hotel users show PEP app activity in the
                  loaded telemetry window. Share the PEP installer with the roster and
                  confirm installation for each user listed below.
                </div>
              )}
              <p className={styles.summaryScopeNote}>
                Counts use the last {ELECTRON_TELEMETRY_FETCH_LOOKBACK_DAYS}-day telemetry
                load. The Timeline date picker only changes the chart and event log below.
              </p>
              {showHealthCheckStaleSignalInsight && (
                <div
                  className={styles.healthCheckInsightBanner}
                  role="region"
                  aria-label="Latest telemetry context from Health Check"
                >
                  <strong>Latest PEP desktop signal in this load</strong>
                  {latestSignalInPayload?.isValid() ? (
                    <p>
                      {formatTime(latestSignalInPayload)} —{' '}
                      {HELP_HEALTH_CHECK_DETAILS_GAP_CONTEXT}
                    </p>
                  ) : (
                    <p>
                      No PEP desktop events were found in the loaded window.{' '}
                      {HELP_HEALTH_CHECK_DETAILS_GAP_CONTEXT}
                    </p>
                  )}
                </div>
              )}
              {hotelUserInstallSummary.withApp === 0 &&
                (data.summary?.deviceCount ?? data.devices?.length ?? 0) > 0 && (
                  <p className={styles.summaryRosterNote}>
                    {'"'}PEP app installed{'"'} counts hotel staff on the roster only.
                    This hotel still has{' '}
                    {data.summary?.deviceCount ?? data.devices?.length} registered PEP
                    device
                    {(data.summary?.deviceCount ?? data.devices?.length) === 1
                      ? ''
                      : 's'}{' '}
                    on other dashboard accounts; see the Users & devices table below.
                  </p>
                )}
              <dl className={styles.summaryGrid}>
                <div className={styles.summaryCard}>
                  <dt>Total hotel users</dt>
                  <dd>
                    {hotelUserInstallSummary.total != null
                      ? hotelUserInstallSummary.total
                      : '—'}
                  </dd>
                </div>
                <div className={styles.summaryCard}>
                  <dt>PEP app installed</dt>
                  <dd>{hotelUserInstallSummary.withApp}</dd>
                </div>
                <div className={styles.summaryCard}>
                  <dt>Not installed</dt>
                  <dd>
                    {hotelUserInstallSummary.withoutApp != null
                      ? hotelUserInstallSummary.withoutApp
                      : '—'}
                  </dd>
                </div>
                <div
                  className={[styles.summaryCard, styles.summaryCardLastActivity].join(
                    ' '
                  )}
                >
                  <dt>Last activity</dt>
                  <dd>{formatTime(data.summary.lastActivityAt)}</dd>
                </div>
                {pepHealthCheckDisplay && (
                  <div
                    className={[
                      styles.summaryCard,
                      styles.summaryCardStatus,
                      pepHealthCheckDisplay.onboardingState === 'Done'
                        ? styles.summaryCardStatusGood
                        : styles.summaryCardStatusWarn
                    ].join(' ')}
                  >
                    <dt>Onboarding</dt>
                    <dd>{pepHealthCheckDisplay.onboardingLabel || 'Unknown'}</dd>
                  </div>
                )}
                {pepHealthCheckDisplay && (
                  <div
                    className={[
                      styles.summaryCard,
                      styles.summaryCardStatus,
                      pepHealthCheckDisplay.futureState === 'Active'
                        ? styles.summaryCardStatusGood
                        : styles.summaryCardStatusWarn
                    ].join(' ')}
                  >
                    <dt>Future</dt>
                    <dd>{pepHealthCheckDisplay.futureLabel || 'Unknown'}</dd>
                  </div>
                )}
              </dl>
            </section>

            <section
              className={styles.section}
              aria-labelledby="electron-telemetry-timeline"
            >
              <h3 id="electron-telemetry-timeline" className={styles.sectionTitle}>
                Timeline
                <SectionInfoButton text={HELP_TIMELINE} />
              </h3>
              <p className={styles.timelineScopeNote}>
                Date range applies to the timeline, session bars, and event log only.
                {hotelTimeZoneLabel
                  ? ` Event, session, and device times are shown in hotel local time (${hotelTimeZoneLabel}).`
                  : ''}
              </p>

              <div className={styles.timelineFiltersRow}>
                {telemetryUsersResolved.length > 0 && (
                  <div
                    className={styles.userChipRow}
                    role="group"
                    aria-label="Filter timeline by user"
                  >
                    <button
                      type="button"
                      className={
                        timelineUserFilter === 'all'
                          ? styles.userChipActive
                          : styles.userChip
                      }
                      onClick={() => {
                        setTimelineUserFilter('all')
                        setSelectedTimelineEvent(null)
                        setSelectedActivityWindow(null)
                      }}
                    >
                      All users
                    </button>
                    {telemetryUsersResolved.map(u => {
                      const key = timelineUserKeyFromTelemetryUser(u)
                      return (
                        <button
                          key={key}
                          type="button"
                          className={
                            timelineUserFilter === key
                              ? styles.userChipActive
                              : styles.userChip
                          }
                          onClick={() => {
                            setTimelineUserFilter(key)
                            setSelectedTimelineEvent(null)
                            setSelectedActivityWindow(null)
                          }}
                        >
                          <span
                            className={styles.userChipDot}
                            style={{
                              background: markerColorForUserKey(
                                key,
                                telemetryUsersResolved.indexOf(u)
                              )
                            }}
                          />
                          {u.displayName ?? u.DisplayName ?? key}
                        </button>
                      )
                    })}
                  </div>
                )}
                <div className={styles.telemetryDatepickerWrap}>
                  <SmartDatepicker
                    isRange={true}
                    startDate={moment(telemetryFromDate)}
                    endDate={moment(telemetryToDate)}
                    onChangeStartDate={d => setTelemetryFromDate(d.format('YYYY-MM-DD'))}
                    onChangeEndDate={d => setTelemetryToDate(d.format('YYYY-MM-DD'))}
                    smartDates={[today, last7days, last14days, last30days]}
                    showMonths={false}
                    showQuarters={false}
                    dismissCurrentDay={false}
                    className={styles.telemetrySmartDatepicker}
                    isAlignedRight={true}
                    appendDropdownToBody={true}
                  />
                </div>
              </div>

              {!hasTimelineContent ? (
                <p className={styles.muted}>No timeline data in this range.</p>
              ) : (
                <div className={styles.timelinePanel}>
                  <div className={styles.timelineChartArea}>
                    <div className={styles.swimlaneAxisHeader}>
                      <div className={styles.swimlaneLabelSpacer} />
                      <div className={styles.timelineAxisLabels}>
                        <span>{formatTimelineBoundaryLabel(timelineRange.start, hotelTimeZoneId)}</span>
                        <span>
                          {Math.abs(hotelNow.diff(timelineRange.end, 'minutes')) < 2
                            ? 'Now'
                            : formatTimelineBoundaryLabel(timelineRange.end, hotelTimeZoneId)}
                        </span>
                      </div>
                    </div>

                    <div className={styles.timelineTrackLegend}>
                      <span className={styles.timelineLegendItem}>
                        <span
                          className={styles.timelineLegendSampleBar}
                          style={
                            timelineLegendActiveReportingColor
                              ? { background: timelineLegendActiveReportingColor }
                              : undefined
                          }
                          aria-hidden
                        />
                        Active reporting (width vs. selected range; hover for exact time)
                      </span>
                      <span
                        className={[
                          styles.timelineLegendItem,
                          styles.timelineLegendItemNoData
                        ].join(' ')}
                      >
                        <span className={styles.timelineLegendSampleGap} aria-hidden />
                        No telemetry in gap (offline, closed, or idle)
                      </span>
                    </div>

                    {visibleUsersForSwimlanes.map((u, ui) => {
                      const userKey = timelineUserKeyFromTelemetryUser(u)
                      const gapMinutes = data.gapThresholdMinutes ?? 15
                      const wins = resolveDisplayActivityWindowsForUser(
                        userKey,
                        windowsForUserKey(activityWindowsInView, userKey),
                        eventsInView,
                        activityWindowsInView,
                        gapMinutes
                      )
                      const hasSignals = userHasTelemetrySignalsInRange(
                        userKey,
                        eventsInView,
                        wins,
                        activityWindowsInView
                      )
                      const paletteIndex = telemetryUsersResolved.findIndex(
                        x => timelineUserKeyFromTelemetryUser(x) === userKey
                      )
                      const color = markerColorForUserKey(
                        userKey,
                        paletteIndex >= 0 ? paletteIndex : ui
                      )
                      const laneTall = timelineUserFilter !== 'all'
                      const laneClass = [
                        laneTall ? styles.activityLaneTall : styles.activityLane,
                        hasSignals ? styles.activityLaneHasSignals : ''
                      ].join(' ')
                      return (
                        <div className={styles.swimlaneRow} key={userKey}>
                          <div className={styles.swimlaneLabel} title={userKey}>
                            {u.displayName ?? u.DisplayName ?? userKey}
                          </div>
                          <div className={laneClass}>
                            <div className={styles.activityLaneInner}>
                              {wins.length === 0 ? (
                                <span className={styles.activityLaneEmpty}>
                                  {hasSignals
                                    ? 'Telemetry present (expand date range for sessions)'
                                    : 'No telemetry in range'}
                                </span>
                              ) : (
                                wins.map((win, wi) => {
                                  const startAt = win.startAt ?? win.StartAt
                                  const endAt = win.endAt ?? win.EndAt
                                  const { clipStart, clipEnd } =
                                    clipActivityWindowToTimelineRange(
                                      win,
                                      timelineRange.start,
                                      timelineRange.end
                                    )
                                  const { left, width } = rangeLeftWidthPercent(
                                    startAt,
                                    endAt,
                                    timelineRange.start,
                                    timelineRange.end
                                  )
                                  const selected = isSameActivityWindow(
                                    selectedActivityWindow,
                                    win
                                  )
                                  const titleStart =
                                    clipStart && clipStart.isValid()
                                      ? clipStart
                                      : moment(startAt)
                                  const titleEnd =
                                    clipEnd && clipEnd.isValid() ? clipEnd : moment(endAt)
                                  return (
                                    <button
                                      key={win.id ?? win.Id ?? `w-${userKey}-${wi}`}
                                      type="button"
                                      className={
                                        selected
                                          ? styles.activitySegmentSelected
                                          : styles.activitySegment
                                      }
                                      style={{
                                        left: `${left}%`,
                                        width: `${width}%`,
                                        background: color
                                      }}
                                      title={`${formatTime(titleStart)} → ${formatTime(
                                        titleEnd
                                      )} · ${formatDurationBetween(
                                        titleStart,
                                        titleEnd
                                      )} active reporting (in selected range)`}
                                      aria-label={`Activity ${formatDurationBetween(
                                        titleStart,
                                        titleEnd
                                      )} in selected range, from ${formatTime(
                                        titleStart
                                      )} to ${formatTime(titleEnd)}`}
                                      onClick={() => {
                                        setSelectedActivityWindow(win)
                                        setSelectedTimelineEvent(null)
                                      }}
                                    />
                                  )
                                })
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>

                  {showTimelineDetailZone && (
                    <div
                      className={styles.timelineDetailZone}
                      role="region"
                      aria-label="Session context and event log"
                    >
                      {timelineUserFilter !== 'all' && (
                        <div className={styles.userProfileStrip}>
                          <div className={styles.userProfileMain}>
                            <span className={styles.userProfileName}>
                              {selectedUserFromFilter?.displayName ??
                                selectedUserFromFilter?.DisplayName ??
                                (timelineUserFilter === 'unassigned'
                                  ? 'Unassigned installation'
                                  : `User ${timelineUserFilter}`)}
                            </span>
                            <span className={styles.userProfileEmail}>
                              {selectedUserFromFilter?.email ??
                                selectedUserFromFilter?.Email ??
                                '—'}
                            </span>
                          </div>
                          <div className={styles.userProfileMeta}>
                            Last seen{' '}
                            {formatTime(
                              selectedUserFromFilter?.lastActivityAt ??
                                selectedUserFromFilter?.LastActivityAt
                            )}
                            {' · '}
                            {selectedUserFromFilter?.eventCount ??
                              selectedUserFromFilter?.EventCount ??
                              '—'}{' '}
                            events · {devicesForTimelineUser.length} device
                            {devicesForTimelineUser.length === 1 ? '' : 's'}
                          </div>
                        </div>
                      )}

                      {(timelineUserFilter !== 'all' || selectedActivityWindow) && (
                        <div className={styles.activityOpsSection}>
                          <div className={styles.activityOpsHeadingBlock}>
                            <div className={styles.activityOpsTitleLine}>
                              <div className={styles.eventLogTitleCluster}>
                                <h4 className={styles.activityOpsTitleRow}>Event log</h4>
                                {timelineUserFilter === 'all' &&
                                  selectedActivityWindow && (
                                    <span className={styles.sessionInfoInline}>
                                      <span className={styles.sessionInfoSep} aria-hidden>
                                        ·
                                      </span>
                                      <strong>
                                        {selectedActivityWindow.label ??
                                          selectedActivityWindow.Label ??
                                          'Activity window'}
                                      </strong>{' '}
                                      <span className={styles.sessionInfoUser}>
                                        {displayNameForTimelineUserKey(
                                          activityWindowUserKey(selectedActivityWindow),
                                          telemetryUsersResolved,
                                          rosterForDisplayNames
                                        )}
                                      </span>
                                      <span className={styles.sessionInfoTime}>
                                        {formatTime(
                                          selectedActivityWindow.startAt ??
                                            selectedActivityWindow.StartAt
                                        )}
                                        {' → '}
                                        {formatTime(
                                          selectedActivityWindow.endAt ??
                                            selectedActivityWindow.EndAt
                                        )}
                                      </span>
                                    </span>
                                  )}
                              </div>
                              <div className={styles.eventLogRowControlWrap}>
                                <label
                                  htmlFor="electron-telemetry-event-log-rows"
                                  className={styles.eventLogRowLabel}
                                >
                                  Rows
                                </label>
                                <select
                                  id="electron-telemetry-event-log-rows"
                                  className={styles.eventLogRowSelect}
                                  value={eventLogRowLimit}
                                  disabled={eventLogEventsLoaded.length === 0}
                                  onChange={e =>
                                    setEventLogRowLimit(Number(e.target.value))
                                  }
                                >
                                  {eventLogRowCountOptions.map(n => (
                                    <option key={n} value={n}>
                                      {n}
                                    </option>
                                  ))}
                                </select>
                                <span
                                  className={styles.eventLogRowMeta}
                                  aria-live="polite"
                                >
                                  Showing {eventLogTableEvents.length} of{' '}
                                  {eventLogEventsLoaded.length}
                                  {eventLogEventsLoaded.length >= EventLogMaxLoadedRows
                                    ? ' (limit reached)'
                                    : ''}
                                </span>
                              </div>
                            </div>
                            <p className={styles.activityOpsSub}>
                              {timelineUserFilter === 'all' && selectedActivityWindow
                                ? `Up to ${EventLogMaxLoadedRows} newest events for the selected bar. Default ${EventLogDefaultDisplayRows} rows; increase with Rows.`
                                : `Up to ${EventLogMaxLoadedRows} newest events for this user in the visible window. Default ${EventLogDefaultDisplayRows} rows; increase with Rows.`}
                            </p>
                          </div>

                          <div className={styles.activityOpsCard}>
                            {eventLogEventsLoaded.length === 0 ? (
                              <p className={styles.activityOpsEmpty}>
                                {timelineUserFilter === 'all' && selectedActivityWindow
                                  ? 'No events in this session window. Try another bar or a user chip.'
                                  : 'No events in this window for this user.'}
                              </p>
                            ) : (
                              <div className={styles.activityOpsTableWrap}>
                                <table className={styles.activityOpsTable}>
                                  <thead>
                                    <tr>
                                      <th scope="col">
                                        Time
                                        {hotelTimeZoneLabel
                                          ? ` (hotel · ${hotelTimeZoneLabel})`
                                          : ''}
                                      </th>
                                      <th scope="col">Event</th>
                                      <th scope="col">Machine</th>
                                      <th scope="col">Details</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {eventLogTableEvents.map(({ data: ev, count, firstAt }) => {
                                      const selected = isSameTimelineEvent(
                                        selectedTimelineEvent,
                                        ev
                                      )
                                      const machineIdentity = machineIdentityForEvent(ev)
                                      const level = logSeverityLevel(ev)
                                      const levelClass =
                                        level === 'error'
                                          ? styles.opsLevelError
                                          : level === 'warn'
                                          ? styles.opsLevelWarn
                                          : styles.opsLevelInfo
                                      return (
                                        <tr
                                          key={
                                            ev.id ??
                                            ev.Id ??
                                            `${ev.occurredAt}-${ev.eventType}`
                                          }
                                          className={
                                            selected
                                              ? styles.activityOpsRowSelected
                                              : styles.activityOpsRow
                                          }
                                          onClick={() => {
                                            setSelectedTimelineEvent(ev)
                                            if (timelineUserFilter === 'all') {
                                              return
                                            }
                                            setSelectedActivityWindow(null)
                                          }}
                                          onKeyDown={e => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                              e.preventDefault()
                                              setSelectedTimelineEvent(ev)
                                              if (timelineUserFilter !== 'all') {
                                                setSelectedActivityWindow(null)
                                              }
                                            }
                                          }}
                                          role="button"
                                          tabIndex={0}
                                        >
                                          <td className={styles.opsCellTime}>
                                            {formatTime(ev.occurredAt ?? ev.OccurredAt)}
                                          </td>
                                          <td className={styles.opsCellEvent}>
                                            <span className={styles.opsCellType}>
                                              {telemetryEventTypeLabel(ev)}
                                            </span>
                                            <span
                                              className={styles.opsCellEventSep}
                                              aria-hidden
                                            >
                                              {' '}
                                              ·{' '}
                                            </span>
                                            <span className={levelClass}>
                                              {severityDisplayLabel(ev)}
                                            </span>
                                          </td>
                                          <td className={styles.opsCellMachine}>
                                            {machineIdentity ? (
                                              <span
                                                className={styles.opsMachineIdentity}
                                                title={`Windows user @ computer: ${machineIdentity}`}
                                              >
                                                {machineIdentity}
                                              </span>
                                            ) : (
                                              <span className={styles.opsMachineEmpty}>—</span>
                                            )}
                                          </td>
                                          <td className={styles.opsCellMsg}>
                                            {operationalMessage(ev)}
                                            {count > 1 && (
                                              <span
                                                className={styles.opsRepeatBadge}
                                                title={
                                                  firstAt
                                                    ? `Repeated ${count} times. First seen ${formatTime(
                                                        firstAt
                                                      )}`
                                                    : `Repeated ${count} times`
                                                }
                                              >
                                                ×{count}
                                              </span>
                                            )}
                                          </td>
                                        </tr>
                                      )
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            )}
                            {(selectedTimelineEvent || selectedActivityWindow) && (
                              <div className={styles.selectionFooter}>
                                <button
                                  type="button"
                                  className={styles.detailClear}
                                  onClick={() => {
                                    setSelectedTimelineEvent(null)
                                    setSelectedActivityWindow(null)
                                  }}
                                >
                                  Clear selection
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </section>

            <section
              className={styles.section}
              aria-labelledby="electron-telemetry-pms-upload-reports"
            >
              <h3
                id="electron-telemetry-pms-upload-reports"
                className={styles.sectionTitle}
              >
                Guest data uploads
                <SectionInfoButton text={guestDataUploadsHelpText} />
              </h3>
              <div className={styles.pepDataRhythmInfoBar} role="status">
                <strong>{pepDataRhythmInfoBar.title}</strong>
                <p>{pepDataRhythmInfoBar.body}</p>
              </div>
              <div className={styles.pmsHistoriesToolbar}>
                <div
                  className={styles.pmsHistoriesTabs}
                  role="tablist"
                  aria-label="Report type"
                >
                  {pmsUploadHistoriesTabListItems.map(({ tabKey, tabLabel }) => (
                    <button
                      key={tabKey}
                      type="button"
                      role="tab"
                      aria-selected={pmsUploadHistoriesActiveTabName === tabKey}
                      className={
                        pmsUploadHistoriesActiveTabName === tabKey
                          ? styles.pmsHistoriesTabActive
                          : styles.pmsHistoriesTab
                      }
                      disabled={pmsUploadHistoriesLoading}
                      onClick={() => setPmsUploadHistoriesActiveTabName(tabKey)}
                    >
                      {tabLabel}
                    </button>
                  ))}
                </div>
                <div className={styles.pmsHistoriesFilters}>
                  <Dropdown
                    value={pmsHistoriesFilterMonth}
                    options={pmsUploadHistoriesMonthDropdownOptions}
                    onChange={e => setPmsHistoriesFilterMonth(Number(e.target.value))}
                    isSortedAlphabetically={false}
                  />
                  <Dropdown
                    value={pmsHistoriesFilterYear}
                    options={pmsUploadHistoriesYearDropdownOptions}
                    onChange={e => setPmsHistoriesFilterYear(Number(e.target.value))}
                    isSortedAlphabetically={false}
                  />
                </div>
              </div>
              {pmsUploadHistoriesLoading && (
                <p className={styles.pmsHistoriesMuted}>Loading upload history…</p>
              )}
              {!pmsUploadHistoriesLoading && pmsUploadHistoriesError && (
                <div className={styles.errorBanner} role="alert">
                  {pmsUploadHistoriesError}
                </div>
              )}
              {!pmsUploadHistoriesLoading &&
                !pmsUploadHistoriesError &&
                pmsUploadHistoriesRaw != null &&
                pmsUploadHistoriesTabListItems.length === 0 && (
                  <p className={styles.pmsHistoriesMuted}>
                    No upload history returned for {uploadLogMonthLabel}.
                  </p>
                )}
              {!pmsUploadHistoriesLoading &&
                !pmsUploadHistoriesError &&
                pmsUploadHistoriesRaw != null &&
                pmsUploadHistoriesTabListItems.length > 0 &&
                pmsUploadHistoriesPager.total > 0 && (
                  <>
                    <h4 className={styles.pmsHistoriesDaySummaryTitle}>
                      Daily upload summary
                    </h4>
                    <div className={styles.pmsHistoriesDayRowsWrap}>
                      {pmsUploadHistoriesPager.slice.map((day, dayRowIndex) => {
                        const logGroup = normalizePmsHistoryDayGroupForUploadLogs(day)
                        return (
                          <UploadLogsDayRow
                            key={logGroup.date || `day-${dayRowIndex}`}
                            logGroup={logGroup}
                            handleDownloadData={undefined}
                            isAdminMode={false}
                            isEditing={false}
                            selectedRows={[]}
                            setSelectedRows={() => {}}
                          />
                        )
                      })}
                    </div>
                    <div className={styles.pmsHistoriesPager}>
                      <span className={styles.pmsHistoriesPagerMeta}>
                        Showing {pmsUploadHistoriesPager.rangeStart}–
                        {pmsUploadHistoriesPager.rangeEnd} of{' '}
                        {pmsUploadHistoriesPager.total} days
                        {pmsUploadHistoriesPager.totalPages > 1 ? (
                          <>
                            {' '}
                            · Page {pmsUploadHistoriesPager.page} of{' '}
                            {pmsUploadHistoriesPager.totalPages}
                          </>
                        ) : null}
                      </span>
                      {pmsUploadHistoriesPager.totalPages > 1 ? (
                        <div className={styles.pmsHistoriesPagerNav}>
                          <button
                            type="button"
                            className={styles.pmsHistoriesPagerBtn}
                            disabled={pmsUploadHistoriesPager.page <= 1}
                            onClick={() =>
                              setPmsUploadHistoriesPage(p => Math.max(1, p - 1))
                            }
                          >
                            Previous
                          </button>
                          <button
                            type="button"
                            className={styles.pmsHistoriesPagerBtn}
                            disabled={
                              pmsUploadHistoriesPager.page >=
                              pmsUploadHistoriesPager.totalPages
                            }
                            onClick={() => {
                              const tp = Math.max(
                                1,
                                Math.ceil(
                                  pmsUploadHistoriesPager.total /
                                    PMS_UPLOAD_HISTORY_PAGE_SIZE
                                )
                              )
                              setPmsUploadHistoriesPage(p => Math.min(tp, p + 1))
                            }}
                          >
                            Next
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </>
                )}
              {!pmsUploadHistoriesLoading &&
                !pmsUploadHistoriesError &&
                pmsUploadHistoriesRaw != null &&
                pmsUploadHistoriesTabListItems.length > 0 &&
                pmsUploadHistoriesActiveDayGroups.length === 0 && (
                  <p className={styles.pmsHistoriesMuted}>
                    No daily rows for the selected report in {uploadLogMonthLabel}.
                  </p>
                )}
            </section>

            <section
              className={styles.section}
              aria-labelledby="electron-telemetry-pep-credentials"
            >
              <h3
                id="electron-telemetry-pep-credentials"
                className={styles.sectionTitle}
              >
                PEP login credentials (stored)
                <SectionInfoButton text={HELP_PEP_LOGIN_CREDENTIALS} />
              </h3>
              <p className={styles.summaryScopeNote}>
                Usernames are plain text. Passwords are encrypted in the database and shown
                here for support copy only. Click a Wrong password / Change password reason to
                open related Hilton diagnostic screenshots when available.
              </p>
              {credentialCaptureNotice ? (
                <p className={styles.credentialCaptureNotice}>{credentialCaptureNotice}</p>
              ) : null}
              {!data.pepLoginCredentials?.length ? (
                <p className={styles.muted}>
                  No stored PEP login credentials for this hotel.
                </p>
              ) : (
                <div className={styles.tableScroll}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Directful hotel</th>
                        <th>PEP hotel</th>
                        <th>Username</th>
                        <th>Encrypted password</th>
                        <th>Primary</th>
                        <th>Status</th>
                        <th>Reason</th>
                        <th>Property</th>
                        <th>Copy</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.pepLoginCredentials.map(row => {
                        const credentialIssue = classifyPepCredentialFailureReason(
                          row.lastFailureReason
                        )
                        const expireDays = parsePasswordExpireDays(row.lastFailureReason)
                        const rowClassName = credentialIssue
                          ? styles.credentialRowIssue
                          : undefined
                        const reasonClickable = Boolean(credentialIssue && row.lastFailureReason)
                        return (
                          <tr key={row.id ?? row.username} className={rowClassName}>
                          <td className={styles.credentialMetaCell} title={row.directfulHotelName}>
                            {row.directfulHotelName || '—'}
                          </td>
                          <td className={styles.credentialMetaCell} title={row.pepHotelName}>
                            {row.pepHotelName || '—'}
                          </td>
                          <td className={styles.credentialUsernameCell}>
                            <code className={styles.opsMessageCode}>{row.username}</code>
                          </td>
                          <td className={styles.credentialEncryptedCell}>
                            <code className={styles.opsMessageCode} title={row.encryptedPassword}>
                              {row.encryptedPassword || '—'}
                            </code>
                          </td>
                          <td>{row.isPrimary ? 'Yes' : 'No'}</td>
                          <td>{row.status || '—'}</td>
                          <td
                            className={`${styles.credentialReasonCell} ${
                              reasonClickable ? styles.credentialReasonClickable : ''
                            }`}
                            title={
                              reasonClickable
                                ? 'Click to open related diagnostic screenshot'
                                : row.lastFailureReason || undefined
                            }
                          >
                            {reasonClickable ? (
                              <button
                                type="button"
                                className={styles.credentialReasonButton}
                                onClick={() => openCredentialReasonCaptures(row)}
                              >
                                {row.lastFailureReason}
                                {expireDays !== null ? (
                                  <span className={styles.credentialExpireHint}>
                                    {` · expires in ${expireDays} day${
                                      expireDays === 1 ? '' : 's'
                                    }`}
                                  </span>
                                ) : null}
                              </button>
                            ) : (
                              row.lastFailureReason || '—'
                            )}
                          </td>
                          <td className={styles.credentialMetaCell} title={row.propertyId}>
                            {row.propertyId || '—'}
                          </td>
                          <td className={styles.credentialCopyCell}>
                            <button
                              type="button"
                              className={styles.credentialCopyBtn}
                              onClick={() => copyTelemetryCredentialField(row.username, 'username')}
                            >
                              User
                            </button>
                            <button
                              type="button"
                              className={styles.credentialCopyBtn}
                              onClick={() =>
                                copyTelemetryCredentialField(
                                  row.encryptedPassword,
                                  'encrypted password'
                                )
                              }
                            >
                              Encrypted
                            </button>
                          </td>
                        </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section
              className={styles.section}
              aria-labelledby="electron-telemetry-diagnostic-captures"
            >
              <h3
                id="electron-telemetry-diagnostic-captures"
                className={styles.sectionTitle}
              >
                Diagnostic captures
                <SectionInfoButton text={HELP_DIAGNOSTIC_CAPTURES} />
              </h3>
              {hotelTimeZoneLabel && (
                <p className={styles.summaryScopeNote}>
                  Times shown in hotel local time ({hotelTimeZoneLabel}).
                </p>
              )}
              {diagnosticCapturesLoading ? (
                <div className={styles.diagnosticCapturesLoading}>
                  <LoadingIndicator />
                </div>
              ) : diagnosticCapturesError ? (
                <p className={styles.muted}>{diagnosticCapturesError}</p>
              ) : !diagnosticCaptures.length ? (
                <p className={styles.muted}>
                  No diagnostic captures for this hotel in the loaded window.
                </p>
              ) : (
                <div className={styles.diagnosticCaptureList}>
                  {pagedDiagnosticCaptures.map(capture => {
                    const captureKey = capture.id ?? `${capture.occurredAt}-${capture.eventCode}`
                    const isExpanded = expandedCaptureIds.has(captureKey)
                    const machineIdentity = captureMachineIdentity(capture)
                    return (
                      <div className={styles.diagnosticCaptureCard} key={captureKey}>
                        <div className={styles.diagnosticCaptureHead}>
                          <span className={styles.diagnosticCaptureTime}>
                            {formatTime(capture.occurredAt)}
                          </span>
                          <span
                            className={styles.diagnosticCaptureCode}
                            title={capture.eventCode}
                          >
                            {capture.eventCode || 'UNKNOWN'}
                          </span>
                          <span
                            className={`${styles.diagnosticCaptureSeverity} ${
                              styles[
                                `severity${(capture.severity || 'Info').toLowerCase()}`
                              ] || ''
                            }`}
                          >
                            {capture.severity || 'Info'}
                          </span>
                        </div>
                        {machineIdentity && (
                          <div className={styles.diagnosticCaptureMeta}>
                            <span title={capture.deviceInstallationId || undefined}>
                              {machineIdentity}
                            </span>
                            {capture.dashboardUserId != null && (
                              <span className={styles.diagnosticCaptureUserId}>
                                User #{capture.dashboardUserId}
                              </span>
                            )}
                          </div>
                        )}
                        {capture.message && (
                          <div className={styles.diagnosticCaptureMessage}>
                            {capture.message}
                          </div>
                        )}
                        <div className={styles.diagnosticCaptureBody}>
                          {capture.hasScreenshot && capture.screenshotUrl ? (
                            <button
                              type="button"
                              className={styles.diagnosticCaptureThumbBtn}
                              onClick={() => setSelectedCaptureScreenshot(capture.screenshotUrl)}
                              title="Click to enlarge screenshot"
                            >
                              <img
                                className={styles.diagnosticCaptureThumb}
                                src={capture.screenshotUrl}
                                alt={`Screenshot for ${capture.eventCode}`}
                              />
                            </button>
                          ) : (
                            <div className={styles.diagnosticCaptureNoShot}>
                              No screenshot
                            </div>
                          )}
                          <div className={styles.diagnosticCaptureJsonWrap}>
                            {(() => {
                              const pageHtml = getCapturePageHtml(capture)
                              const har = getCaptureHar(capture)
                              return (
                                <div className={styles.diagnosticCaptureArtifactActions}>
                                  {pageHtml && (
                                    <>
                                      <button
                                        type="button"
                                        className={styles.diagnosticCaptureArtifactBtn}
                                        onClick={() => setSelectedCaptureHtml(pageHtml)}
                                      >
                                        View HTML
                                        {isPageHtmlTruncated(capture) ? ' (truncated)' : ''}
                                      </button>
                                      <button
                                        type="button"
                                        className={styles.diagnosticCaptureArtifactBtn}
                                        onClick={() =>
                                          downloadTextFile(
                                            buildDiagnosticArtifactFilename(
                                              capture.eventCode,
                                              capture.id,
                                              'html'
                                            ),
                                            pageHtml,
                                            'text/html;charset=utf-8'
                                          )
                                        }
                                      >
                                        Download HTML
                                      </button>
                                    </>
                                  )}
                                  {har && (
                                    <>
                                      <button
                                        type="button"
                                        className={styles.diagnosticCaptureArtifactBtn}
                                        onClick={() => {
                                          try {
                                            setSelectedCaptureHarText(
                                              JSON.stringify(har, null, 2)
                                            )
                                          } catch {
                                            setSelectedCaptureHarText(
                                              'Unable to display HAR.'
                                            )
                                          }
                                        }}
                                      >
                                        View HAR
                                        {isHarTruncated(capture) ? ' (truncated)' : ''}
                                      </button>
                                      <button
                                        type="button"
                                        className={styles.diagnosticCaptureArtifactBtn}
                                        onClick={() => {
                                          try {
                                            downloadTextFile(
                                              buildDiagnosticArtifactFilename(
                                                capture.eventCode,
                                                capture.id,
                                                'har'
                                              ),
                                              JSON.stringify(har, null, 2),
                                              'application/json;charset=utf-8'
                                            )
                                          } catch {
                                            // ignore download failures
                                          }
                                        }}
                                      >
                                        Download HAR
                                      </button>
                                    </>
                                  )}
                                </div>
                              )
                            })()}
                            <button
                              type="button"
                              className={styles.diagnosticCaptureJsonToggle}
                              onClick={() =>
                                setExpandedCaptureIds(prev => {
                                  const next = new Set(prev)
                                  if (next.has(captureKey)) {
                                    next.delete(captureKey)
                                  } else {
                                    next.add(captureKey)
                                  }
                                  return next
                                })
                              }
                            >
                              {isExpanded ? 'Hide JSON context' : 'Show JSON context'}
                            </button>
                            {isExpanded && (
                              <pre className={styles.diagnosticCaptureJson}>
                                {capture.jsonText || 'No JSON context available.'}
                              </pre>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
              {!diagnosticCapturesLoading &&
                !diagnosticCapturesError &&
                diagnosticCaptures.length > DIAGNOSTIC_CAPTURES_PAGE_SIZE && (
                  <div className={styles.diagnosticCapturesPager}>
                    <button
                      type="button"
                      className={styles.diagnosticCapturesPagerBtn}
                      disabled={diagnosticCapturesPage <= 1}
                      onClick={() =>
                        setDiagnosticCapturesPage(prev => Math.max(1, prev - 1))
                      }
                    >
                      Previous
                    </button>
                    <span className={styles.diagnosticCapturesPagerLabel}>
                      {`Page ${diagnosticCapturesPage} of ${diagnosticCapturesPageCount}`}
                    </span>
                    <button
                      type="button"
                      className={styles.diagnosticCapturesPagerBtn}
                      disabled={diagnosticCapturesPage >= diagnosticCapturesPageCount}
                      onClick={() =>
                        setDiagnosticCapturesPage(prev =>
                          Math.min(diagnosticCapturesPageCount, prev + 1)
                        )
                      }
                    >
                      Next
                    </button>
                  </div>
                )}
              {selectedCaptureScreenshot && (
                <div
                  className={styles.diagnosticCaptureOverlay}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedCaptureScreenshot(null)}
                  onKeyDown={e => {
                    if (e.key === 'Escape' || e.key === 'Enter') {
                      setSelectedCaptureScreenshot(null)
                    }
                  }}
                >
                  <img
                    className={styles.diagnosticCaptureOverlayImage}
                    src={selectedCaptureScreenshot}
                    alt="Diagnostic capture screenshot"
                  />
                </div>
              )}
              {selectedCaptureHtml && (
                <div
                  className={styles.diagnosticCaptureOverlay}
                  role="dialog"
                  aria-label="Diagnostic capture HTML"
                  onClick={() => setSelectedCaptureHtml(null)}
                  onKeyDown={e => {
                    if (e.key === 'Escape') {
                      setSelectedCaptureHtml(null)
                    }
                  }}
                >
                  <div
                    className={styles.diagnosticCaptureHtmlPanel}
                    onClick={e => e.stopPropagation()}
                    onKeyDown={e => e.stopPropagation()}
                    role="document"
                  >
                    <div className={styles.diagnosticCaptureArtifactHeader}>
                      <span>Page HTML (sandboxed)</span>
                      <button
                        type="button"
                        className={styles.diagnosticCaptureArtifactBtn}
                        onClick={() => setSelectedCaptureHtml(null)}
                      >
                        Close
                      </button>
                    </div>
                    <iframe
                      className={styles.diagnosticCaptureHtmlFrame}
                      title="Diagnostic capture HTML"
                      sandbox={DIAGNOSTIC_HTML_IFRAME_SANDBOX}
                      srcDoc={selectedCaptureHtml}
                    />
                  </div>
                </div>
              )}
              {selectedCaptureHarText && (
                <div
                  className={styles.diagnosticCaptureOverlay}
                  role="dialog"
                  aria-label="Diagnostic capture HAR"
                  onClick={() => setSelectedCaptureHarText(null)}
                  onKeyDown={e => {
                    if (e.key === 'Escape') {
                      setSelectedCaptureHarText(null)
                    }
                  }}
                >
                  <div
                    className={styles.diagnosticCaptureHarPanel}
                    onClick={e => e.stopPropagation()}
                    onKeyDown={e => e.stopPropagation()}
                    role="document"
                  >
                    <div className={styles.diagnosticCaptureArtifactHeader}>
                      <span>HAR</span>
                      <button
                        type="button"
                        className={styles.diagnosticCaptureArtifactBtn}
                        onClick={() => setSelectedCaptureHarText(null)}
                      >
                        Close
                      </button>
                    </div>
                    <pre className={styles.diagnosticCaptureHarPre}>
                      {selectedCaptureHarText}
                    </pre>
                  </div>
                </div>
              )}
            </section>

            <section
              className={styles.section}
              aria-labelledby="electron-telemetry-users"
            >
              <h3 id="electron-telemetry-users" className={styles.sectionTitle}>
                Users &amp; devices
                <SectionInfoButton text={HELP_USERS_DEVICES} />
              </h3>
              {unifiedUserDeviceRows.length === 0 ? (
                <p className={styles.muted}>No users or devices.</p>
              ) : (
                <div className={styles.tableScroll}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Email</th>
                        <th>Device / user</th>
                        <th>Version</th>
                        <th>Install</th>
                        <th>Last seen</th>
                        <th>Dashboard login</th>
                        <th>PEP login</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unifiedUserDeviceRows.map(row => (
                        <tr key={row.rowKey}>
                          <td className={styles.userCell}>
                            <div className={styles.userCellName}>{row.userName}</div>
                            <PepInstallStatusBadge
                              kind={row.kind}
                              deviceSignalInSelectedRange={
                                row.deviceSignalInSelectedRange
                              }
                            />
                          </td>
                          <td className={styles.emailCell} title={row.email ?? undefined}>
                            {row.email}
                          </td>
                          <td className={styles.deviceIdCell}>
                            {row.deviceId ? (
                              <>
                                {row.deviceIdentity && (
                                  <div
                                    className={styles.deviceIdentityLine}
                                    title={`Windows user @ computer: ${row.deviceIdentity}`}
                                  >
                                    <svg
                                      className={styles.deviceIdentityIcon}
                                      width="12"
                                      height="12"
                                      viewBox="0 0 16 16"
                                      fill="currentColor"
                                      aria-hidden="true"
                                    >
                                      <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm0 1.5c-3 0-5.5 1.5-5.5 3.5V14h11v-1c0-2-2.5-3.5-5.5-3.5Z" />
                                    </svg>
                                    <span className={styles.deviceIdentityText}>
                                      {row.deviceIdentity}
                                    </span>
                                  </div>
                                )}
                                <code
                                  className={styles.deviceIdCode}
                                  title={row.deviceId}
                                >
                                  {formatDeviceInstallationIdForTable(row.deviceId)}
                                </code>
                                {row.kind === 'device' && row.lastClientEventPreview && (
                                  <div
                                    className={styles.lastClientEventLine}
                                    title={row.lastClientEventPreview}
                                  >
                                    <span className={styles.lastClientEventTime}>
                                      {formatTime(row.lastClientEventOccurredAt)}
                                    </span>
                                    <span className={styles.lastClientEventSep}> · </span>
                                    <span>{row.lastClientEventPreview}</span>
                                  </div>
                                )}
                                {row.kind === 'device' &&
                                  row.accessibleHotels?.raw && (
                                    <button
                                      type="button"
                                      className={styles.accessibleHotelsBtn}
                                      onClick={() =>
                                        setSelectedAccessibleHotels({
                                          userName: row.userName,
                                          deviceId: row.deviceId,
                                          hotels: row.accessibleHotels
                                        })
                                      }
                                      title="View the hotels this PEP user is authorized for (raw PEP mini-objects)"
                                    >
                                      {`PEP hotels${
                                        row.accessibleHotels.list?.length
                                          ? ` (${row.accessibleHotels.list.length})`
                                          : ''
                                      }`}
                                    </button>
                                  )}
                              </>
                            ) : (
                              <span className={styles.deviceIdEmpty}>—</span>
                            )}
                          </td>
                          <td>{row.appVersion ?? '—'}</td>
                          <td>{formatTime(row.firstLogAt)}</td>
                          <td>{formatTime(row.lastSeenAt)}</td>
                          <td>
                            <LoginStatusBadge
                              active={row.dashboardLoginActive}
                              stale={isLoginStatusStaleForRow(row)}
                              label="Dashboard web session active on this device"
                            />
                          </td>
                          <td>
                            <LoginStatusBadge
                              active={row.pepLoginActive}
                              stale={isLoginStatusStaleForRow(row)}
                              label="PEP portal session active on this device"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {selectedAccessibleHotels && (
                <div
                  className={styles.accessibleHotelsOverlay}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedAccessibleHotels(null)}
                  onKeyDown={e => {
                    if (e.key === 'Escape' || e.key === 'Enter') {
                      setSelectedAccessibleHotels(null)
                    }
                  }}
                >
                  <div
                    className={styles.accessibleHotelsDialog}
                    role="dialog"
                    aria-label="PEP authorized hotels"
                    onClick={e => e.stopPropagation()}
                  >
                    <div className={styles.accessibleHotelsDialogHead}>
                      <div className={styles.accessibleHotelsDialogTitle}>
                        PEP authorized hotels
                        <span className={styles.accessibleHotelsDialogSub}>
                          {selectedAccessibleHotels.userName}
                          {selectedAccessibleHotels.deviceId
                            ? ` · ${selectedAccessibleHotels.deviceId}`
                            : ''}
                        </span>
                      </div>
                      <button
                        type="button"
                        className={styles.accessibleHotelsDialogClose}
                        onClick={() => setSelectedAccessibleHotels(null)}
                        title="Close"
                      >
                        ×
                      </button>
                    </div>
                    {selectedAccessibleHotels.hotels.list?.length > 0 && (
                      <ul className={styles.accessibleHotelsList}>
                        {selectedAccessibleHotels.hotels.list.map((hotel, idx) => (
                          <li
                            key={`${hotel.id ?? 'na'}-${idx}`}
                            className={styles.accessibleHotelsListItem}
                          >
                            <span className={styles.accessibleHotelsName}>
                              {hotel.name ?? '—'}
                            </span>
                            {hotel.id && (
                              <code className={styles.accessibleHotelsId}>
                                {hotel.id}
                              </code>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                    <details className={styles.accessibleHotelsRawWrap}>
                      <summary className={styles.accessibleHotelsRawToggle}>
                        Raw JSON
                      </summary>
                      <pre className={styles.accessibleHotelsRaw}>
                        {selectedAccessibleHotels.hotels.raw}
                      </pre>
                    </details>
                  </div>
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </ConfirmationDialog>
  )
}

export default ElectronTelemetryModal
